/**
 * TestScene - Simple test scene to demonstrate physics and collision
 */

import * as THREE from 'three';
import type { Scene } from '../../types/systems';
import type { Entity } from '../../types/entities';
import { getPhysicsEngine } from '../../engine/PhysicsEngine';
import { getCollisionSystem } from '../../engine/CollisionSystem';
import { getGameLoop } from '../../engine/GameLoop';
import { getInputManager } from '../../engine/InputManager';
import { getInteractionManager } from '../../engine/InteractionManager';
import { Robot } from '../entities/Robot';
import { Player } from '../entities/Player';
import { Collectible } from '../entities/Collectible';
import type { CollectibleType } from '../entities/Collectible';
import { Door } from '../entities/Door';
import { Vent } from '../entities/Vent';
import { HidingSpot } from '../entities/HidingSpot';
import { SecurityCamera } from '../entities/SecurityCamera';
import { InteractiveObject } from '../entities/InteractiveObject';
import { SpawnPoint } from '../entities/SpawnPoint';
import { Elevator } from '../entities/Elevator';
import type { Vector2 } from '../../types/game';
import { debugSettings } from '../../utils/DebugSettings';
import { playJumpSound, playPlayerHitSound, playTaserPickup, playDetectionBeep, playDetectionFull } from '../audio/SoundEffects';
import { audioManager } from '../../engine/AudioManager';
import { upgradeSystem } from '../systems/UpgradeSystem';
import { inventorySystem } from '../systems/InventorySystem';
import { CheckpointSystem } from '../systems/CheckpointSystem';
import { VisualEffects } from '../systems/VisualEffects';
import { AmbientAudioSystem } from '../systems/AmbientAudioSystem';
import { getSaveManager } from '../../engine/SaveManager';
import { store } from '../../state/store';
import {
  setHealth,
  setStamina,
  setFlashlightBattery,
  setPosition,
  setCrouching,
  setSprinting,
  setMask,
  setFlashlight,
  setHiding,
  takeDamage,
} from '../../state/slices/playerSlice';
import { addNotification, clearNotifications } from '../../state/slices/uiSlice';

export class TestScene implements Scene {
  public id: string = 'test';
  public name: string = 'Physics Test Scene';

  // Three.js objects
  private scene: THREE.Scene;
  private camera: THREE.OrthographicCamera;
  private renderer: THREE.WebGLRenderer;

  // Test entities
  public player: Player;
  private platforms: Entity[] = [];
  private robots: Robot[] = [];

  // Damage system
  private damageCooldowns: Map<string, number> = new Map(); // Track cooldown per robot
  private robotLostSightTimers: Map<string, number> = new Map();   // seconds since robot last saw player
  private robotDetectCooldowns: Map<string, number> = new Map();   // seconds until robot can detect again
  private robotSuspicionMeters: Map<string, number> = new Map();   // 0–100; reaches 100 to trigger chase
  private robotDetectionBeepTimers: Map<string, number> = new Map(); // seconds since last detection beep per robot
  // Damage cooldown shrinks per night: Night 1 = 1000ms, Night 7 = 600ms
  private get DAMAGE_COOLDOWN_MS(): number {
    return Math.max(600, 1000 - (this.currentNight - 1) * 65);
  }
  private onPlayerDeathCallback: ((cause: string) => void) | null = null;
  private collectibles: Collectible[] = [];
  private doors: Door[] = [];
  private vents: Vent[] = [];
  private elevators: Elevator[] = [];
  private hidingSpots: HidingSpot[] = [];
  private securityCameras: SecurityCamera[] = [];
  private interactiveObjects: InteractiveObject[] = [];
  private spawnPoints: SpawnPoint[] = [];
  private playerMesh!: THREE.Mesh;
  private platformMeshes: THREE.Mesh[] = [];
  private robotMeshes: THREE.Mesh[] = [];
  private collectibleMeshes: THREE.Mesh[] = [];

  // Systems
  private physicsEngine = getPhysicsEngine();
  private collisionSystem = getCollisionSystem();
  private gameLoop = getGameLoop();
  private inputManager = getInputManager();
  private interactionManager = getInteractionManager();
  public checkpointSystem = CheckpointSystem.getInstance();
  private saveManager = getSaveManager();

  // Visual effects system (particles + screen shake)
  private visualEffects!: VisualEffects;
  private ambientAudio!: AmbientAudioSystem;
  private playerWasGrounded: boolean = false;
  private robotsInChase: Set<string> = new Set();
  private suspicionOverlay: HTMLDivElement | null = null;

  // Store paused sounds for resume (with their playback positions)
  private pausedSounds: Array<{ sound: any; seek: number }> = [];

  // Game state tracking
  private currentNight: number = 1;
  private playStartTime: number = Date.now();
  private currentSaveSlot: number = 1; // Auto-save slot

  // Notification queue system - prevents overlapping notifications
  private notificationQueue: Array<{ checkpointName: string }> = [];
  private isShowingNotification: boolean = false;
  private currentNotificationElement: HTMLDivElement | null = null;

  // Door navigation history - tracks where the player came from for bidirectional travel
  // Key = doorId, Value = the position to return to when exiting through this door
  private doorReturnPositions: Map<string, { x: number; y: number }> = new Map();

  // Robot door teleport tracking — key = `${robotId}:${doorId}`, value = position to return to
  private robotDoorReturnPositions: Map<string, { x: number; y: number }> = new Map();

  // Taser / combat state
  private taserCooldown: number = 10;        // seconds remaining until next shot
  private crosshairEl: HTMLDivElement | null = null;
  private onMouseDownBound: ((e: MouseEvent) => void) | null = null;

  // Game over conditions
  private idleTimer: number = 0;
  private lastIdlePosition: { x: number; y: number } = { x: -400, y: 50 };
  private hasEnteredBuilding: boolean = false;
  private buildingEntryTimer: number = 0;
  public isGameOverPending: boolean = false;
  // Building entry timeout: 120s on Night 1 (tutorial grace), tightens to 60s by Night 7
  private get BUILDING_ENTRY_TIMEOUT(): number {
    return Math.max(60, 120 - (this.currentNight - 1) * 10);
  }
  private readonly IDLE_TIMEOUT: number = 45;            // seconds standing still = game over
  private readonly BUILDING_ENTRY_X: number = 0;         // x > 0 means player is inside

  // Background meshes that follow camera (static background effect)
  private backgroundMeshes: THREE.Mesh[] = [];

  // Elevator call buttons (one per floor, outside the shaft)
  private callButtons: Array<{
    floorIndex: number;
    x: number;
    y: number;
    mesh: THREE.Group;
    lightMesh: THREE.Mesh;
  }> = [];

  // Shaft gates: solid floor-gap blockers that activate when elevator is away
  private shaftGates: Array<{
    floorIndex: number; // which floor's gap this covers (1 = F2 gap, 2 = F3 gap)
    entity: Entity;
    mesh: THREE.Mesh;
    isRegistered: boolean;
  }> = [];

  // Hidden searchable containers (lockers, desks, boxes, etc.)
  private hiddenContainers: Array<{
    id: string;
    x: number; y: number;
    label: string;           // e.g. "Search locker", shown as E-hint
    itemId: string | null;   // inventory item id granted on search (null = empty)
    itemName: string;        // display name for notification
    minNight: number;        // item only present if currentNight >= minNight
    searched: boolean;
    mesh: THREE.Mesh;
  }> = [];

  // Elevator lock — true while player is riding a moving elevator
  private playerInMovingElevator: boolean = false;

  // Security camera UI state (FNAF-style monitor)
  private cameraUIOpen: boolean = false;
  private cameraUIElement: HTMLDivElement | null = null;
  private selectedCameraIndex: number = 0;
  private cameraUIKeyHandler: ((e: KeyboardEvent) => void) | null = null;
  private camFeedFrameCount: number = 0;

  private static readonly CAMERA_NAMES = [
    'Lobby', 'Dining Hall', 'Stage', 'Arcade', 'Play Place', 'Party Room A', 'Party Room C',
    'Kitchen', 'Backstage', 'Break Room', 'Security Office', "Manager's Office", 'Storage',
    'Animatronics', 'Maintenance', 'Server Room', 'Observation', 'Outside'
  ];
  private static readonly CAMERA_LABELS = [
    'CAM 1A', 'CAM 1B', 'CAM 1C', 'CAM 1D', 'CAM 1E', 'CAM 1F', 'CAM 1G',
    'CAM 2A', 'CAM 2B', 'CAM 2C', 'CAM 2D', 'CAM 2E', 'CAM 2F',
    'CAM 3A', 'CAM 3B', 'CAM 3C', 'CAM 3D', 'CAM OUT'
  ];

  // World-space room bounds + simplified furniture for FNAF-style camera feed rendering
  // Item coords are Three.js center-based (same as platform data). c = CSS color string.
  private static readonly CAMERA_ROOM_DEFS: Array<{
    wx: number; wy: number; ww: number; wh: number; fy: number;
    items: Array<{ x: number; y: number; w: number; h: number; c: string }>;
  }> = [
    // 0: Lobby (CAM 1A) — Floor 1, x=-200..300
    { wx:-200, wy:0, ww:500, wh:400, fy:20, items:[
      {x:-70,y:43,w:100,h:40,c:'#5a3d1a'}, {x:100,y:43,w:90,h:35,c:'#3d2b0f'},
      {x:160,y:43,w:70,h:35,c:'#3d2b0f'}, {x:-180,y:23,w:35,h:6,c:'#7a1a1a'},
    ]},
    // 1: Dining Hall (CAM 1B)
    { wx:300, wy:0, ww:500, wh:400, fy:20, items:[
      {x:400,y:85,w:80,h:28,c:'#7a5218'}, {x:550,y:85,w:80,h:28,c:'#7a5218'},
      {x:700,y:85,w:80,h:28,c:'#7a5218'}, {x:470,y:75,w:30,h:100,c:'#b07808'},
    ]},
    // 2: Stage (CAM 1C)
    { wx:800, wy:0, ww:600, wh:400, fy:20, items:[
      {x:1100,y:50,w:300,h:50,c:'#3a0060'}, {x:900,y:115,w:30,h:180,c:'#700000'},
      {x:1300,y:115,w:30,h:180,c:'#700000'}, {x:1000,y:108,w:40,h:30,c:'#444'},
      {x:1100,y:108,w:40,h:30,c:'#444'},    {x:1200,y:108,w:40,h:30,c:'#444'},
    ]},
    // 3: Arcade (CAM 1D)
    { wx:1400, wy:0, ww:800, wh:400, fy:20, items:[
      {x:1530,y:75,w:55,h:110,c:'#111155'}, {x:1650,y:75,w:55,h:110,c:'#115511'},
      {x:1770,y:75,w:55,h:110,c:'#551111'}, {x:1890,y:75,w:55,h:110,c:'#113355'},
      {x:2050,y:75,w:55,h:110,c:'#331155'}, {x:1800,y:230,w:160,h:35,c:'#994400'},
    ]},
    // 4: Play Place (CAM 1E)
    { wx:2400, wy:0, ww:500, wh:400, fy:20, items:[
      {x:2600,y:60,w:160,h:35,c:'#aa0066'}, {x:2750,y:100,w:100,h:150,c:'#1a8a1a'},
      {x:2600,y:130,w:70,h:90,c:'#998800'},
    ]},
    // 5: Party Room A (CAM 1F)
    { wx:2900, wy:0, ww:600, wh:400, fy:20, items:[
      {x:3200,y:95,w:180,h:30,c:'#997700'}, {x:3050,y:220,w:22,h:70,c:'#aa2266'},
      {x:3350,y:220,w:22,h:70,c:'#336699'}, {x:3200,y:300,w:160,h:14,c:'#882211'},
    ]},
    // 6: Party Room C (CAM 1G)
    { wx:4100, wy:0, ww:700, wh:400, fy:20, items:[
      {x:4450,y:95,w:150,h:30,c:'#997700'}, {x:4250,y:220,w:22,h:70,c:'#aa2266'},
      {x:4650,y:220,w:22,h:70,c:'#336699'},
    ]},
    // 7: Kitchen (CAM 2A) — Floor 2, x=-200..500
    { wx:-200, wy:400, ww:700, wh:400, fy:421, items:[
      {x:-50,y:439,w:140,h:35,c:'#999'}, {x:200,y:449,w:70,h:55,c:'#111'},
      {x:-170,y:476,w:55,h:110,c:'#ccc'},{x:100,y:435,w:120,h:28,c:'#aaa'},
      {x:380,y:444,w:70,h:45,c:'#aaa'},
    ]},
    // 8: Backstage (CAM 2B)
    { wx:500, wy:400, ww:600, wh:400, fy:421, items:[
      {x:555,y:444,w:60,h:45,c:'#554428'}, {x:650,y:466,w:90,h:90,c:'#664433'},
      {x:900,y:481,w:70,h:120,c:'#664433'},{x:1040,y:435,w:45,h:28,c:'#333'},
      {x:800,y:436,w:50,h:30,c:'#444'},
    ]},
    // 9: Break Room (CAM 2C)
    { wx:1100, wy:400, ww:1100, wh:400, fy:421, items:[
      {x:1278,y:437,w:110,h:32,c:'#6b4212'},{x:1507,y:437,w:80,h:32,c:'#6b4212'},
      {x:1250,y:435,w:40,h:28,c:'#554428'},{x:1390,y:435,w:40,h:28,c:'#554428'},
      {x:1714,y:461,w:55,h:80,c:'#111'},  {x:1900,y:471,w:70,h:100,c:'#444'},
      {x:2050,y:434,w:35,h:25,c:'#bbb'},
    ]},
    // 10: Security Office (CAM 2D)
    { wx:2400, wy:400, ww:800, wh:400, fy:421, items:[
      {x:2720,y:444,w:140,h:45,c:'#3a2010'},{x:2720,y:570,w:200,h:90,c:'#04041a'},
      {x:3050,y:454,w:55,h:65,c:'#444'},   {x:2500,y:435,w:35,h:28,c:'#222'},
    ]},
    // 11: Manager's Office (CAM 2E)
    { wx:3200, wy:400, ww:800, wh:400, fy:421, items:[
      {x:3500,y:444,w:140,h:45,c:'#3a2010'},{x:3800,y:520,w:65,h:140,c:'#5a2808'},
      {x:3395,y:435,w:35,h:30,c:'#222'},   {x:3300,y:445,w:45,h:30,c:'#333'},
    ]},
    // 12: Storage (CAM 2F)
    { wx:4000, wy:400, ww:800, wh:400, fy:421, items:[
      {x:4150,y:486,w:70,h:130,c:'#554020'},{x:4350,y:486,w:70,h:130,c:'#554020'},
      {x:4550,y:466,w:60,h:90,c:'#554020'},{x:4250,y:432,w:50,h:22,c:'#6a3010'},
      {x:4480,y:431,w:40,h:20,c:'#6a3010'},
    ]},
    // 13: Animatronics Workshop (CAM 3A) — Floor 3, x=-200..1000
    { wx:-200, wy:800, ww:1200, wh:400, fy:820, items:[
      {x:-50,y:870,w:130,h:45,c:'#252525'},{x:400,y:870,w:130,h:45,c:'#252525'},
      {x:800,y:870,w:130,h:45,c:'#252525'},{x:880,y:895,w:80,h:130,c:'#180808'},
      {x:-150,y:878,w:40,h:65,c:'#555'},  {x:-80,y:898,w:35,h:25,c:'#aa0000'},
      {x:370,y:898,w:30,h:22,c:'#aa0000'},
    ]},
    // 14: Maintenance (CAM 3B)
    { wx:1000, wy:800, ww:1200, wh:400, fy:820, items:[
      {x:1200,y:870,w:80,h:65,c:'#444'},{x:1600,y:900,w:90,h:120,c:'#333'},
      {x:2000,y:855,w:45,h:40,c:'#444'},
    ]},
    // 15: Server Room (CAM 3C)
    { wx:2400, wy:800, ww:1200, wh:400, fy:820, items:[
      {x:2600,y:910,w:55,h:130,c:'#080820'},{x:2800,y:910,w:55,h:130,c:'#080820'},
      {x:3000,y:910,w:55,h:130,c:'#080820'},{x:3200,y:910,w:55,h:130,c:'#080820'},
      {x:3400,y:910,w:55,h:130,c:'#080820'},{x:2600,y:980,w:12,h:6,c:'#00ff00'},
      {x:2800,y:990,w:12,h:6,c:'#ff0000'},
    ]},
    // 16: Observation (CAM 3D)
    { wx:3600, wy:800, ww:1200, wh:400, fy:820, items:[
      {x:3850,y:900,w:160,h:80,c:'#081020'},{x:4200,y:900,w:160,h:80,c:'#081020'},
      {x:4550,y:900,w:160,h:80,c:'#081020'},{x:3950,y:860,w:130,h:40,c:'#1e1e2e'},
    ]},
    // 17: Outside (CAM OUT)
    { wx:-1000, wy:-100, ww:800, wh:400, fy:-10, items:[
      {x:-500,y:120,w:14,h:240,c:'#444'},{x:-500,y:250,w:35,h:18,c:'#999933'},
      {x:-800,y:120,w:14,h:240,c:'#444'},{x:-350,y:50,w:80,h:55,c:'#1a3a1a'},
    ]},
  ];

  // Player sprite textures
  private playerTextures: {
    idle: THREE.Texture | null;
    walkFrames: THREE.Texture[];
    jump: THREE.Texture | null;
  } = { idle: null, walkFrames: [], jump: null };
  private currentPlayerTexture: string = 'idle';
  private walkFrameIndex: number = 0;
  private walkFrameTimer: number = 0;
  private readonly WALK_FRAME_DURATION: number = 0.07; // seconds per frame (~14fps)

  constructor(nightNumber: number = 1) {
    // Set the current night
    this.currentNight = nightNumber;

    // Initialize Three.js scene
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x050510); // Very dark night sky

    // Create orthographic camera for 2D view - Sized for SPACIOUS 3-FLOOR layout
    const aspect = window.innerWidth / window.innerHeight;
    const frustumSize = 800; // Wide view for the expanded building
    this.camera = new THREE.OrthographicCamera(
      (frustumSize * aspect) / -2,
      (frustumSize * aspect) / 2,
      frustumSize / 2,
      frustumSize / -2,
      1,
      1000
    );
    this.camera.position.set(500, 150, 500); // Center on Ground Floor public area

    // Create renderer
    const canvas = document.getElementById('game-canvas') as HTMLCanvasElement;
    if (!canvas) {
      throw new Error('Canvas element not found');
    }

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);

    // Create test entities
    this.player = this.createPlayer();
    this.platforms = this.createPlatforms();
    this.robots = this.createRobots();
    this.collectibles = this.createCollectibles();
    this.createHiddenContainers();
    this.doors = this.createDoors();
    this.vents = this.createVents();
    this.elevators = this.createElevators();
    this.createShaftGates();
    this.createCallButtons();
    this.hidingSpots = this.createHidingSpots();
    this.securityCameras = this.createSecurityCameras();
    this.interactiveObjects = this.createInteractiveObjects();
    this.spawnPoints = this.createSpawnPoints();

    // Create background (stars, moon, city silhouette)
    this.createBackground();

    // Initialize visual effects (particles + screen shake)
    this.visualEffects = new VisualEffects(this.scene);
    this.ambientAudio = new AmbientAudioSystem();
    this.createSuspicionOverlay();
    this.createCrosshair();

    // Initialize SaveManager
    this.saveManager.initialize();

    // Setup checkpoint activation callback for visual feedback and auto-save
    this.checkpointSystem.setOnCheckpointActivated((checkpoint) => {
      const checkpointName = checkpoint.getCheckpointName();
      console.log(`✅ CHECKPOINT ACTIVATED: ${checkpointName}`);

      // Auto-save game progress
      this.saveGameProgress(checkpointName);

      // Queue the notification instead of showing immediately
      this.queueNotification(checkpointName);
    });

    // Setup collision callback for damage and debugging
    this.collisionSystem.setCollisionCallback((entityA, entityB, info) => {
      if (debugSettings.enableCollisionLogging) {
        console.log(`Collision: ${entityA} <-> ${entityB}, normal: (${info.normal.x}, ${info.normal.y}), penetration: ${info.penetration}`);
      }

      // Check for player-robot collision (damage system)
      const isPlayerA = entityA === 'player-1';
      const isPlayerB = entityB === 'player-1';
      const isRobotA = entityA.startsWith('robot-');
      const isRobotB = entityB.startsWith('robot-');

      if ((isPlayerA && isRobotB) || (isPlayerB && isRobotA)) {
        if (this.playerInMovingElevator) return;
        const robotId = isRobotA ? entityA : entityB;

        // Check damage cooldown for this specific robot
        const now = Date.now();
        const lastDamageTime = this.damageCooldowns.get(robotId) || 0;

        if (now - lastDamageTime >= this.DAMAGE_COOLDOWN_MS) {
          // Use the robot's actual damage value (instant-kill robots deal 9999)
          const robotEntity = this.robots.find(r => r.id === robotId);
          const damage = robotEntity ? robotEntity.getDamage() : 25;
          const isInstantKill = robotEntity ? robotEntity.instantKill : false;

          this.player.takeDamage(damage);
          store.dispatch(takeDamage(damage));
          playPlayerHitSound();

          // Visual feedback: hit particles + screen shake
          this.visualEffects.spawnHitParticles(this.player.position.x, this.player.position.y);
          this.visualEffects.triggerScreenShake(isInstantKill ? 12 : 6, 0.35);
          this.flashDamageOverlay();

          this.damageCooldowns.set(robotId, now);

          if (isInstantKill) {
            console.log(`☠️ Player instantly killed by ${robotId}!`);
          } else {
            console.log(`💥 Player hit by ${robotId}! Took ${damage} damage. Health: ${this.player.health}`);
          }

          if (this.player.health <= 0) {
            console.log('💀 Player died!');
            if (this.onPlayerDeathCallback) {
              this.onPlayerDeathCallback('Caught by a robot');
            }
          }
        }
      }
    });

    // Setup interaction manager
    this.setupInteractionManager();

    console.log('TestScene: Initialized');
  }

  /**
   * Create player entity
   */
  private createPlayer(): Player {
    const player = new Player(
      'player-1',
      { x: -400, y: 50 }, // Spawn OUTSIDE in the parking lot
      { width: 48, height: 48 }
    );

    // Initialize player (registers with physics and collision systems)
    player.initialize();

    // Load player sprite textures
    const textureLoader = new THREE.TextureLoader();
    const spritePath = 'assets/sprites/player/';

    const loadPlayerTexture = (filename: string): THREE.Texture => {
      const texture = textureLoader.load(spritePath + filename);
      texture.magFilter = THREE.NearestFilter; // Pixel-perfect scaling
      texture.minFilter = THREE.NearestFilter;
      texture.colorSpace = THREE.SRGBColorSpace;
      return texture;
    };

    this.playerTextures.idle = loadPlayerTexture('p1_stand.png');
    this.playerTextures.jump = loadPlayerTexture('p1_jump.png');
    for (let i = 1; i <= 11; i++) {
      const padded = String(i).padStart(2, '0');
      this.playerTextures.walkFrames.push(loadPlayerTexture(`p1_walk${padded}.png`));
    }

    // Create visual representation - Textured sprite quad
    const geometry = new THREE.PlaneGeometry(48, 48); // Slightly larger for visibility
    const material = new THREE.MeshBasicMaterial({
      map: this.playerTextures.idle,
      transparent: true,
      alphaTest: 0.1,
    });
    this.playerMesh = new THREE.Mesh(geometry, material);
    this.playerMesh.renderOrder = 10; // Render player above platforms
    this.scene.add(this.playerMesh);

    return player;
  }

  /**
   * Create platform entities - KHAN'S PIZZA PALACE LAYOUT
   * SPACIOUS LAYOUT - 5000 units wide, 3 floors, 400 height per floor
   *
   * LAYOUT (Side-View Cross Section):
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * ROOF + PIZZA SIGN
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * ║ ANIMATRONICS     ║ MAINTENANCE       ║  [ELEVATOR]  ║ SERVER ROOM       ║ OBS DECK  ║  F3
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * ║ KITCHEN  ║ BACKSTAGE ║ BREAK ROOM ║  [ELEVATOR]  ║ SECURITY ║ MANAGER ║ STORAGE   ║  F2
   * ═══════════════════════════════════════════════════════════════════════════════════════
   * ║ LOBBY ║ DINING ║ STAGE  ║ ARCADE ║  [ELEVATOR]  ║ PLAY  ║ PARTY A ║ PARTY B ║ PARTY C ║  F1
   * ═══════════════════════════════════════════════════════════════════════════════════════
   *    PARKING LOT / OUTSIDE
   *
   * Building: x=-200 to x=4800 (5000 wide)
   * Elevator shaft at center: x=2200..2400 (200 wide)
   * Floor heights: F1=0, F2=400, F3=800, Roof=1200
   * Room height: 400 each (plenty of vertical space)
   */
  private createPlatforms(): Entity[] {
    const platformsData: Array<{ x: number; y: number; width: number; height: number; color: number; name: string }> = [
      // ═══════════════════════════════════════
      // GROUND - extends far beyond building
      // ═══════════════════════════════════════
      { x: 2300, y: -10, width: 8000, height: 60, color: 0x333333, name: 'full_ground' },
      // Parking lot surface
      { x: -600, y: -5, width: 800, height: 30, color: 0x444444, name: 'parking_surface' },
      { x: -500, y: -15, width: 80, height: 6, color: 0x888888, name: 'parking_line_1' },
      { x: -700, y: -15, width: 80, height: 6, color: 0x888888, name: 'parking_line_2' },
      // Streetlamps
      { x: -500, y: 120, width: 14, height: 240, color: 0x777777, name: 'streetlamp_pole_1' },
      { x: -500, y: 250, width: 35, height: 18, color: 0xFFFF88, name: 'streetlamp_light_1' },
      { x: -800, y: 120, width: 14, height: 240, color: 0x777777, name: 'streetlamp_pole_2' },
      { x: -800, y: 250, width: 35, height: 18, color: 0xFFFF88, name: 'streetlamp_light_2' },
      // Dumpster
      { x: -350, y: 50, width: 80, height: 55, color: 0x3A5F3A, name: 'dumpster' },

      // ═══════════════════════════════════════
      // BUILDING STRUCTURE - 3 FLOORS (5000 wide, 400 per floor)
      // Building spans: x=-200 to x=4800
      // ═══════════════════════════════════════

      // Floor 1 slab (ground level)
      { x: 2300, y: 0, width: 5000, height: 40, color: 0x8B6914, name: 'f1_floor' },
      // Floor 2 slab (y=400) - split with gap at elevator shaft (x=2180 to x=2420)
      { x: 990, y: 400, width: 2380, height: 30, color: 0x696969, name: 'f1_ceiling_left' },
      { x: 3610, y: 400, width: 2380, height: 30, color: 0x696969, name: 'f1_ceiling_right' },
      // Floor 3 slab (y=800) - split with gap at elevator shaft (x=2180 to x=2420)
      { x: 990, y: 800, width: 2380, height: 30, color: 0x696969, name: 'f2_ceiling_left' },
      { x: 3610, y: 800, width: 2380, height: 30, color: 0x696969, name: 'f2_ceiling_right' },
      // Roof (y=1200)
      { x: 2300, y: 1200, width: 5000, height: 30, color: 0x3D2B1F, name: 'roof' },

      // Neon pizza sign on roof
      { x: 1200, y: 1250, width: 600, height: 50, color: 0xFF4500, name: 'neon_sign' },
      { x: 1200, y: 1270, width: 540, height: 8, color: 0xFFD700, name: 'sign_stripe' },

      // Left exterior wall (full building height)
      { x: -210, y: 620, width: 20, height: 1280, color: 0x8B3A3A, name: 'left_ext_wall' },
      // Right exterior wall
      { x: 4810, y: 620, width: 20, height: 1280, color: 0x8B3A3A, name: 'right_ext_wall' },

      // ═══════════════════════════════════════
      // ELEVATOR SHAFT WALLS (x=2200 to x=2400, all 3 floors)
      // Openings at floor 2 (y=415–535) and floor 3 (y=815–935) for room access
      // Floor 1 is open at the bottom (walls start at y=120)
      // ═══════════════════════════════════════
      // Left shaft wall - 3 segments with floor 2 & 3 openings
      { x: 2190, y: 267.5, width: 20, height: 295, color: 0x4A4A5A, name: 'shaft_left_1' },  // y=120–415
      { x: 2190, y: 675,   width: 20, height: 280, color: 0x4A4A5A, name: 'shaft_left_2' },  // y=535–815
      { x: 2190, y: 1060, width: 20, height: 250, color: 0x4A4A5A, name: 'shaft_left_3' },   // y=935–1185 (stops at roof)
      // Right shaft wall - 3 segments with floor 2 & 3 openings
      { x: 2410, y: 267.5, width: 20, height: 295, color: 0x4A4A5A, name: 'shaft_right_1' }, // y=120–415
      { x: 2410, y: 675,   width: 20, height: 280, color: 0x4A4A5A, name: 'shaft_right_2' }, // y=535–815
      { x: 2410, y: 1060,  width: 20, height: 250, color: 0x4A4A5A, name: 'shaft_right_3' }, // y=935–1185 (stops at roof)

      // ═══════════════════════════════════════
      // FLOOR 1 - INTERIOR WALLS (thick, visible, floor-to-ceiling)
      // Room widths ~500 each, elevator gap at 2200-2400
      // Left side:  Lobby(-200..300), Dining(300..800), Stage(800..1400), Arcade(1400..2200)
      // Right side: Play(2400..2900), PartyA(2900..3500), PartyB(3500..4100), PartyC(4100..4800)
      // Walls are 20px thick, span from floor (y=20) to ceiling (y=400)
      // ═══════════════════════════════════════
      { x: 300, y: 210, width: 20, height: 380, color: 0xD2B48C, name: 'f1_wall_1' },
      { x: 800, y: 210, width: 20, height: 380, color: 0xD2B48C, name: 'f1_wall_2' },
      { x: 1400, y: 210, width: 20, height: 380, color: 0xD2B48C, name: 'f1_wall_3' },
      { x: 2900, y: 210, width: 20, height: 380, color: 0xD2B48C, name: 'f1_wall_5' },
      { x: 3500, y: 210, width: 20, height: 380, color: 0xD2B48C, name: 'f1_wall_6' },
      { x: 4100, y: 210, width: 20, height: 380, color: 0xD2B48C, name: 'f1_wall_7' },

      // ═══════════════════════════════════════
      // FLOOR 2 - INTERIOR WALLS
      // Left side:  Kitchen(-200..500), Backstage(500..1100), BreakRoom(1100..2200)
      // Right side: Security(2400..3200), Manager(3200..4000), Storage(4000..4800)
      // ═══════════════════════════════════════
      { x: 500, y: 610, width: 20, height: 380, color: 0x808080, name: 'f2_wall_1' },
      { x: 1100, y: 610, width: 20, height: 380, color: 0x808080, name: 'f2_wall_2' },
      { x: 3200, y: 610, width: 20, height: 380, color: 0x808080, name: 'f2_wall_4' },
      { x: 4000, y: 610, width: 20, height: 380, color: 0x808080, name: 'f2_wall_5' },

      // ═══════════════════════════════════════
      // FLOOR 3 - INTERIOR WALLS
      // Left side:  Animatronics(-200..1000), Maintenance(1000..2200)
      // Right side: Server(2400..3600), Observation(3600..4800)
      // ═══════════════════════════════════════
      { x: 1000, y: 1010, width: 20, height: 380, color: 0x4A0000, name: 'f3_wall_1' },
      { x: 3600, y: 1010, width: 20, height: 380, color: 0x4A0000, name: 'f3_wall_3' },

      // ═══════════════════════════════════════
      // FLOOR 1 - ROOM CONTENTS (spacious, larger objects)
      // ═══════════════════════════════════════

      // --- LOBBY (-200 to 300) ---
      { x: 50, y: 24, width: 490, height: 6, color: 0xDEB887, name: 'lobby_tile' },
      { x: -70, y: 43, width: 100, height: 40, color: 0x8B7355, name: 'front_desk' },
      { x: -180, y: 24, width: 35, height: 6, color: 0xCD5C5C, name: 'welcome_mat' },
      { x: 100, y: 43, width: 90, height: 35, color: 0x8B7355, name: 'lobby_couch' },
      { x: 160, y: 43, width: 70, height: 35, color: 0x8B7355, name: 'lobby_couch_2' },

      // --- DINING AREA (300 to 800) ---
      { x: 550, y: 24, width: 490, height: 6, color: 0xDEB887, name: 'dining_tile' },
      { x: 400, y: 95, width: 80, height: 28, color: 0xCD853F, name: 'table_1' },
      { x: 550, y: 95, width: 80, height: 28, color: 0xCD853F, name: 'table_2' },
      { x: 700, y: 95, width: 80, height: 28, color: 0xCD853F, name: 'table_3' },
     /// { x: 400, y: 200, width: 70, height: 80, color: 0x8B0000, name: 'booth_1' },
      ///{ x: 550, y: 200, width: 70, height: 80, color: 0x8B0000, name: 'booth_2' },
      ///{ x: 700, y: 200, width: 70, height: 80, color: 0x8B0000, name: 'booth_3' }, 
      { x: 470, y: 75, width: 30, height: 100, color: 0xDAA520, name: 'service_counter' },

      // --- SHOW STAGE (800 to 1400) ---
      { x: 1100, y: 24, width: 590, height: 6, color: 0x1A0A2E, name: 'stage_tile' },
      { x: 1100, y: 50, width: 300, height: 50, color: 0x4B0082, name: 'stage_platform' },
      { x: 900, y: 115, width: 30, height: 180, color: 0x8B0000, name: 'curtain_L' },
      { x: 1300, y: 115, width: 30, height: 180, color: 0x8B0000, name: 'curtain_R' },
      { x: 1100, y: 310, width: 250, height: 10, color: 0xFFD700, name: 'stage_lights' },
      { x: 1000, y: 108, width: 40, height: 30, color: 0x555555, name: 'pedestal_1' },
      { x: 1100, y: 108, width: 40, height: 30, color: 0x555555, name: 'pedestal_2' },
      { x: 1200, y: 108, width: 40, height: 30, color: 0x555555, name: 'pedestal_3' },

      // --- ARCADE (1400 to 2200) ---
      { x: 1800, y: 24, width: 790, height: 6, color: 0x0D0D2B, name: 'arcade_tile' },
      { x: 1530, y: 75, width: 55, height: 110, color: 0x1A1A6A, name: 'arcade_cab_1' },
      { x: 1650, y: 75, width: 55, height: 110, color: 0x2A6A2A, name: 'arcade_cab_2' },
      { x: 1770, y: 75, width: 55, height: 110, color: 0x6A2A2A, name: 'arcade_cab_3' },
      { x: 1890, y: 75, width: 55, height: 110, color: 0x1A4A6A, name: 'arcade_cab_4' },
      { x: 2050, y: 75, width: 55, height: 110, color: 0x4A2A6A, name: 'arcade_cab_5' },
      { x: 1800, y: 230, width: 160, height: 35, color: 0xFF8C00, name: 'prize_counter' },
      { x: 1800, y: 320, width: 140, height: 14, color: 0x00FFFF, name: 'arcade_neon' },

      // --- PLAY PLACE (2400 to 2900) ---
      { x: 2650, y: 24, width: 490, height: 6, color: 0x1A3D1A, name: 'play_tile' },
      { x: 2600, y: 60, width: 160, height: 35, color: 0xFF1493, name: 'ball_pit' },
      { x: 2750, y: 100, width: 100, height: 150, color: 0x32CD32, name: 'play_structure' },
      { x: 2600, y: 130, width: 70, height: 90, color: 0xFFD700, name: 'slide' },
      //{ x: 2850, y: 55, width: 50, height: 30, color: 0x00BFFF, name: 'play_bench' },

      // --- PARTY ROOM A (2900 to 3500) ---
      { x: 3200, y: 24, width: 590, height: 6, color: 0x3D1A3D, name: 'partya_tile' },
      { x: 3200, y: 95, width: 180, height: 30, color: 0xFFD700, name: 'party_table_a' },
      { x: 3050, y: 220, width: 22, height: 70, color: 0xFF69B4, name: 'balloon_a1' },
      { x: 3350, y: 220, width: 22, height: 70, color: 0x87CEEB, name: 'balloon_a2' },
      { x: 3200, y: 300, width: 160, height: 14, color: 0xFF6347, name: 'banner_a' },

      // --- PARTY ROOM B (3500 to 4100) ---
      { x: 3800, y: 24, width: 590, height: 6, color: 0x3D1A3D, name: 'partyb_tile' },
      { x: 3800, y: 95, width: 160, height: 30, color: 0xFFD700, name: 'party_table_b' },
      { x: 3650, y: 220, width: 22, height: 70, color: 0xFF69B4, name: 'balloon_b1' },
      { x: 3950, y: 220, width: 22, height: 70, color: 0x87CEEB, name: 'balloon_b2' },
      { x: 3800, y: 300, width: 160, height: 14, color: 0xFF6347, name: 'banner_b' },

      // --- PARTY ROOM C (4100 to 4800) ---
      { x: 4450, y: 24, width: 690, height: 6, color: 0x3D1A3D, name: 'partyc_tile' },
      { x: 4450, y: 95, width: 150, height: 30, color: 0xFFD700, name: 'party_table_c' },
      { x: 4250, y: 220, width: 22, height: 70, color: 0xFF69B4, name: 'balloon_c1' },
      { x: 4650, y: 220, width: 22, height: 70, color: 0x87CEEB, name: 'balloon_c2' },

      // ═══════════════════════════════════════
      // FLOOR 2 - ROOM CONTENTS (Employees Only)
      // ═══════════════════════════════════════
      // employees_stripe removed (was solid platform blocking player movement)

      // --- KITCHEN (-200 to 500) ---  floor tile top=421, items: center_y = 421 + h/2
      { x: 150, y: 418, width: 690, height: 6, color: 0xA9A9A9, name: 'kitchen_tile' },
      { x: -50,  y: 439, width: 140, height: 35,  color: 0xC0C0C0, name: 'kitchen_counter' },
      { x: 200,  y: 449, width: 70,  height: 55,  color: 0x1A1A1A, name: 'stove' },
      { x: -170, y: 476, width: 55,  height: 110, color: 0xD3D3D3, name: 'fridge' },
      { x: 100,  y: 435, width: 120, height: 28,  color: 0xC0C0C0, name: 'prep_table' },
      { x: 380,  y: 444, width: 70,  height: 45,  color: 0xC0C0C0, name: 'dish_rack' },

      // --- BACKSTAGE (500 to 1100) ---
      { x: 800, y: 418, width: 590, height: 6, color: 0x3C3C3C, name: 'backstage_tile' },
      { x: 650,  y: 530, width: 60, height: 45,  color: 0x696969, name: 'workbench' },
      { x: 650,  y: 466, width: 90, height: 90,  color: 0x8B7355, name: 'costume_rack' },
      { x: 900,  y: 481, width: 70, height: 120, color: 0x8B7355, name: 'backstage_shelves' },
      { x: 1040, y: 435, width: 45, height: 28,  color: 0x555555, name: 'parts_box' },
      { x: 800,  y: 436, width: 50, height: 30,  color: 0x696969, name: 'tool_box' },

      // --- EMPLOYEE BREAK ROOM (1100 to 2200) ---
      { x: 1650, y: 418, width: 1090, height: 6, color: 0x2F2F1F, name: 'breakroom_tile' },
      { x: 1328, y: 487, width: 110, height: 32, color: 0xCD853F, name: 'break_table' },
      { x: 1507, y: 487, width: 80,  height: 32, color: 0xCD853F, name: 'break_table_2' },
      { x: 1250, y: 435, width: 40,  height: 28, color: 0x8B7355, name: 'break_chair_1' },
      { x: 1390, y: 435, width: 40,  height: 28, color: 0x8B7355, name: 'break_chair_2' },
      { x: 1714, y: 461, width: 55,  height: 80, color: 0x1A1A1A, name: 'vending_machine' },
      { x: 1900, y: 471, width: 70,  height: 100,color: 0x696969, name: 'lockers' },
      { x: 2050, y: 434, width: 35,  height: 25, color: 0xD3D3D3, name: 'microwave' },

      // --- SECURITY OFFICE (2400 to 3200) ---
      { x: 2800, y: 418, width: 790, height: 6, color: 0x2F3D2F, name: 'security_tile' },
      { x: 2720, y: 444, width: 140, height: 45, color: 0x5C4033, name: 'security_desk' },
      { x: 2720, y: 570, width: 200, height: 90, color: 0x0A0A2A, name: 'monitor_bank' },
      { x: 3050, y: 454, width: 55,  height: 65, color: 0x696969, name: 'filing_cabinet' },
      { x: 2500, y: 435, width: 35,  height: 28, color: 0x333333, name: 'security_chair' },

      // --- MANAGER'S OFFICE (3200 to 4000) ---
      { x: 3600, y: 418, width: 790, height: 6, color: 0x3D2F1F, name: 'manager_tile' },
      { x: 3500, y: 444, width: 140, height: 45, color: 0x5C4033, name: 'manager_desk' },
      { x: 3800, y: 495, width: 65,  height: 140,color: 0x8B4513, name: 'bookshelf' },
      { x: 3395, y: 435, width: 35,  height: 30, color: 0x2F2F2F, name: 'manager_chair' },
      { x: 3300, y: 435, width: 45,  height: 30, color: 0x555555, name: 'safe' },

      // --- STORAGE (4000 to 4800) ---
      { x: 4400, y: 418, width: 790, height: 6, color: 0x404040, name: 'storage_tile' },
      { x: 4150, y: 486, width: 70, height: 130, color: 0x8B7355, name: 'storage_shelf_1' },
      { x: 4350, y: 486, width: 70, height: 130, color: 0x8B7355, name: 'storage_shelf_2' },
      { x: 4550, y: 466, width: 60, height: 90,  color: 0x8B7355, name: 'storage_shelf_3' },
      { x: 4700, y: 466, width: 60, height: 90,  color: 0x8B7355, name: 'storage_shelf_4' },
      { x: 4250, y: 432, width: 50, height: 22,  color: 0xA0522D, name: 'crate_1' },
      { x: 4480, y: 431, width: 40, height: 20,  color: 0xA0522D, name: 'crate_2' },

      // ═══════════════════════════════════════
      // FLOOR 3 - RESTRICTED AREA
      // ═══════════════════════════════════════
      // restricted_stripe removed (was solid platform blocking player movement)

      // ═══════════════════════════════════════
      // ANIMATRONICS WORKSHOP (-200 to 1000) - SCARY ROOM
      // ═══════════════════════════════════════
      { x: 400, y: 818, width: 1190, height: 6, color: 0x1A0000, name: 'anim_tile' },
      // Bloody floor patches
      { x: -50, y: 820, width: 80, height: 5, color: 0x8B0000, name: 'blood_1' },
      { x: 400, y: 820, width: 100, height: 5, color: 0x6B0000, name: 'blood_2' },
      { x: 800, y: 820, width: 70, height: 5, color: 0x8B0000, name: 'blood_3' },
      // Workbenches with robot parts - well spaced
      { x: -50, y: 870, width: 130, height: 45, color: 0x3B3B3B, name: 'anim_bench_1' },
      { x: 400, y: 870, width: 130, height: 45, color: 0x3B3B3B, name: 'anim_bench_2' },
      { x: 800, y: 870, width: 130, height: 45, color: 0x3B3B3B, name: 'anim_bench_3' },
      // Robot parts on benches
      { x: -80, y: 898, width: 35, height: 25, color: 0xFF0000, name: 'robot_head_1' },
      { x: -10, y: 900, width: 28, height: 18, color: 0xCC0000, name: 'robot_arm_1' },
      { x: 370, y: 898, width: 30, height: 22, color: 0xFF0000, name: 'robot_head_2' },
      { x: 440, y: 900, width: 25, height: 16, color: 0xCC0000, name: 'robot_arm_2' },
      { x: 770, y: 898, width: 35, height: 25, color: 0xFF0000, name: 'robot_head_3' },
      { x: 840, y: 900, width: 28, height: 18, color: 0xCC0000, name: 'robot_arm_3' },
      // Chains from ceiling
      { x: -100, y: 1050, width: 8, height: 200, color: 0x555555, name: 'chain_1' },
      { x: 150, y: 1060, width: 8, height: 160, color: 0x555555, name: 'chain_2' },
      { x: 350, y: 1040, width: 8, height: 200, color: 0x555555, name: 'chain_3' },
      { x: 550, y: 1070, width: 8, height: 130, color: 0x555555, name: 'chain_4' },
      { x: 700, y: 1050, width: 8, height: 180, color: 0x555555, name: 'chain_5' },
      { x: 900, y: 1060, width: 8, height: 160, color: 0x555555, name: 'chain_6' },
      // Warning signs
      { x: -180, y: 980, width: 45, height: 30, color: 0xFFAA00, name: 'warn_sign_1' },
      { x: 960, y: 980, width: 45, height: 30, color: 0xFFAA00, name: 'warn_sign_2' },
      // Blood stains on walls
      { x: -195, y: 940, width: 18, height: 80, color: 0x4A0000, name: 'wall_blood_1' },
      { x: -195, y: 1080, width: 18, height: 50, color: 0x3A0000, name: 'wall_blood_2' },
      // Dim lights
      { x: 200, y: 1160, width: 100, height: 10, color: 0x554400, name: 'dim_light_1' },
      { x: 650, y: 1160, width: 100, height: 10, color: 0x554400, name: 'dim_light_2' },
      // Creepy cabinet
      { x: 880, y: 895, width: 80, height: 130, color: 0x2A1A1A, name: 'creepy_cabinet' },
      // Endoskeleton
      { x: -150, y: 878, width: 40, height: 65, color: 0x888888, name: 'endoskeleton' },

      // --- MAINTENANCE CORRIDOR (1000 to 2200) ---
      { x: 1600, y: 818, width: 1190, height: 6, color: 0x2A2A2A, name: 'maint_tile' },
      { x: 1200, y: 870, width: 80, height: 65, color: 0x696969, name: 'utility_box' },
      { x: 1600, y: 990, width: 90, height: 120, color: 0x555555, name: 'pipe_junction' },
      { x: 2000, y: 855, width: 45, height: 40, color: 0x696969, name: 'fuse_box' },
      { x: 1400, y: 990, width: 14, height: 230, color: 0x4A4A4A, name: 'pipe_v1' },
      { x: 1800, y: 1000, width: 14, height: 210, color: 0x4A4A4A, name: 'pipe_v2' },

      // --- SERVER ROOM (2400 to 3600) ---
      { x: 3000, y: 818, width: 1190, height: 6, color: 0x0A0A2A, name: 'server_tile' },
      { x: 2600, y: 910, width: 55, height: 130, color: 0x1A1A3A, name: 'srv_rack_1' },
      { x: 2800, y: 910, width: 55, height: 130, color: 0x1A1A3A, name: 'srv_rack_2' },
      { x: 3000, y: 910, width: 55, height: 130, color: 0x1A1A3A, name: 'srv_rack_3' },
      { x: 3200, y: 910, width: 55, height: 130, color: 0x1A1A3A, name: 'srv_rack_4' },
      { x: 3400, y: 910, width: 55, height: 130, color: 0x1A1A3A, name: 'srv_rack_5' },
      { x: 2600, y: 980, width: 12, height: 6, color: 0x00FF00, name: 'srv_led_1' },
      { x: 2800, y: 990, width: 12, height: 6, color: 0xFF0000, name: 'srv_led_2' },
      { x: 3000, y: 975, width: 12, height: 6, color: 0x00FF00, name: 'srv_led_3' },
      { x: 3200, y: 985, width: 12, height: 6, color: 0x00FFFF, name: 'srv_led_4' },
      { x: 3400, y: 980, width: 12, height: 6, color: 0x00FF00, name: 'srv_led_5' },
      { x: 3000, y: 1060, width: 800, height: 10, color: 0x333333, name: 'cable_tray' },

      // --- OBSERVATION DECK (3600 to 4800) ---
      { x: 4200, y: 818, width: 1190, height: 6, color: 0x1A1A2A, name: 'obs_tile' },
      { x: 3850, y: 900, width: 160, height: 80, color: 0x1A2A4A, name: 'obs_window_1' },
      { x: 4200, y: 900, width: 160, height: 80, color: 0x1A2A4A, name: 'obs_window_2' },
      { x: 4550, y: 900, width: 160, height: 80, color: 0x1A2A4A, name: 'obs_window_3' },
      { x: 3950, y: 860, width: 130, height: 40, color: 0x3A3A4A, name: 'control_panel' },
      { x: 4050, y: 850, width: 30, height: 25, color: 0x333333, name: 'obs_chair_1' },
      { x: 4300, y: 850, width: 30, height: 25, color: 0x333333, name: 'obs_chair_2' },
      { x: 4550, y: 880, width: 55, height: 80, color: 0x696969, name: 'obs_filing' },
    ];

    const platforms: Entity[] = [];

    platformsData.forEach((data, index) => {
      const platform: Entity = {
        id: `platform-${index}`,
        type: 'static',
        position: { x: data.x, y: data.y },
        velocity: { x: 0, y: 0 },
        size: { width: data.width, height: data.height },
        rotation: 0,
        active: true,
        destroyed: false,
      };

      // Create visual representation with color-coded areas
      const geometry = new THREE.BoxGeometry(data.width, data.height, 1);
      const material = new THREE.MeshBasicMaterial({
        color: data.color,
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(data.x, data.y, 0);
      this.scene.add(mesh);
      this.platformMeshes.push(mesh);

      // Add to physics engine (kinematic - not affected by physics)
      this.physicsEngine.addEntity(platform, {
        mass: 1.0,
        gravity: 0,
        friction: 0.8,
        restitution: 0,
        isKinematic: true,
        isGrounded: false,
        canJump: false,
      });

      // Add to collision system
      this.collisionSystem.addCollider(platform, {
        type: 'box',
        isTrigger: false,
        layer: 'static',
      });

      platforms.push(platform);
    });

    return platforms;
  }

  /**
   * Create robot entities based on current night
   * Night 1: 1 robot, then +1-2 robots per night
   */
  private createRobots(): Robot[] {
    // 3 distinct robot types introduced progressively by night:
    // Night 1: Stalker only (red, standard)
    // Night 2+: Stalker + Crawler (blue, ceiling/wall, instant kill)
    // Night 3+: Stalker + Crawler + Phantom (purple, vent traversal)
    const allRobotSpawns = [
      { x: 1100, y: 90,  type: 'stalker' as const },   // F1 Stage
      { x: 400,  y: 870, type: 'crawler' as const },   // F3 Workshop ceiling
      { x: 1800, y: 50,  type: 'phantom' as const },   // F1 Arcade
    ];

    const robotCount = Math.min(this.currentNight, allRobotSpawns.length);
    console.log(`🤖 Night ${this.currentNight}: Spawning ${robotCount} robot(s)`);

    const robotsData = allRobotSpawns.slice(0, robotCount);
    const robots: Robot[] = [];

    robotsData.forEach((data, index) => {
      const robot = new Robot(
        `robot-${index}`,
        data.type,
        { x: data.x, y: data.y },
        { width: 32, height: 48 }
      );

      // Patrol paths per robot type
      let patrolPath: Vector2[];

      if (data.type === 'stalker') {
        // Ground patrol: Stage → Dining → Lobby → Arcade
        patrolPath = [
          { x: 1100, y: 90 },
          { x: 550,  y: 50 },
          { x: 50,   y: 50 },
          { x: 1800, y: 50 },
          { x: 1100, y: 90 },
        ];
      } else if (data.type === 'crawler') {
        // Ceiling patrol: Workshop top, Maintenance top, back
        // Y values near ceiling of F3 (~1060) and F2 (~670)
        patrolPath = [
          { x: 400,  y: 1060 },
          { x: 1600, y: 1060 },
          { x: 2000, y: 670  },
          { x: 800,  y: 670  },
          { x: 400,  y: 1060 },
        ];
      } else {
        // Phantom: Arcade → Backstage → Security (uses vents)
        patrolPath = [
          { x: 1800, y: 50  },
          { x: 800,  y: 450 },
          { x: 2800, y: 450 },
          { x: 1800, y: 50  },
        ];
      }

      robot.setPatrolPath(patrolPath);
      // First robot (stalker) is the door-opener; others have their own gimmicks
      if (index === 0) robot.canOpenDoors = true;

      // ── Per-night difficulty scaling ───────────────────────────────────────
      // Each night adds 10% speed and 8% detection range, capped at Night 7 (1.8x).
      // Night 1 = 1.0x baseline, Night 7 = 1.6x speed, 1.48x detection.
      const nightMult = 1 + (this.currentNight - 1) * 0.1;
      const detMult   = 1 + (this.currentNight - 1) * 0.08;
      robot.moveSpeed     = Math.round(robot.moveSpeed  * nightMult);
      robot.chaseSpeed    = Math.round(robot.chaseSpeed * nightMult);
      robot.detectionRange = Math.round(robot.detectionRange * detMult);
      // Damage scales only from Night 4 onward (+5 per night)
      if (this.currentNight >= 4 && !robot.instantKill) {
        robot.damage += (this.currentNight - 3) * 5;
      }

      robot.initialize();

      // Map robot type → scary PNG sprite
      const spriteMap: Record<string, { file: string; w: number; h: number }> = {
        stalker: { file: 'enemy_nightmare_1.png', w: 72, h: 82 },
        crawler: { file: 'enemy_specter_1.png',   w: 72, h: 91 },
        phantom: { file: 'enemy_inferno_1.png',   w: 72, h: 93 },
      };
      const spriteInfo = spriteMap[data.type] ?? { file: 'enemy_nightmare_1.png', w: 72, h: 82 };

      const texLoader = new THREE.TextureLoader();
      const tex = texLoader.load(`assets/sprites/robots/${spriteInfo.file}?v=${Date.now()}`);
      tex.magFilter = THREE.NearestFilter;
      tex.minFilter = THREE.NearestFilter;
      tex.colorSpace = THREE.SRGBColorSpace;

      const geometry = new THREE.PlaneGeometry(spriteInfo.w, spriteInfo.h);
      const material = new THREE.MeshBasicMaterial({
        map: tex,
        transparent: true,
        alphaTest: 0.5,
      });
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(data.x, data.y, 0.5);
      mesh.renderOrder = 5;
      this.scene.add(mesh);
      this.robotMeshes.push(mesh);

      robots.push(robot);
    });

    return robots;
  }

  /**
   * Create collectible entities - Pizza Place layout
   */
  private createCollectibles(): Collectible[] {
    // ── Special fixed collectibles (always present, all nights) ─────────────
    // Keys: Floor 1, party rooms (x=2900-4800, y≈65)
    // Taser: Floor 2, employees-only (Kitchen x≈200-500, y≈440)
    // Paper/lore: Floor 2, Manager's Office (x=3200-4000, y≈430)
    //
    // ── Variable collectibles — night-gated with growing increment ───────────
    // Night 1: 3   Night 2: +4=7   Night 3: +5=12  Night 4: +6=18
    // Night 5: +7=25  Night 6: +8=33  Night 7: +9=42  (increment caps at 10)
    // Spread across all 3 floors so every floor has items to find.
    //
    // Floor Y positions (standing surface):
    //   Floor 1 public ≈ y=65   Floor 2 employee ≈ y=440   Floor 3 restricted ≈ y=860

    type CollEntry = { x: number; y: number; type: CollectibleType; minNight: number };

    const allCollectibles: CollEntry[] = [
      // ─────────────────────────────────────────────────────────────────────
      // SPECIAL FIXED COLLECTIBLES (minNight=1, always visible)
      // ─────────────────────────────────────────────────────────────────────
      // Keys — Floor 1 party rooms
      { x: 3100, y: 65,  type: 'key',   minNight: 1 }, // Party Room A
      { x: 4250, y: 65,  type: 'key',   minNight: 1 }, // Party Room C
      // Taser — Floor 2 employee-only (Kitchen area)
      { x: 250,  y: 445, type: 'taser', minNight: 1 },
      // Paper/lore — Floor 2 Manager's Office
      { x: 3450, y: 430, type: 'lore',  minNight: 1 },

      // ─────────────────────────────────────────────────────────────────────
      // NIGHT 1 — 3 variable collectibles, Floor 1 only
      // ─────────────────────────────────────────────────────────────────────
      { x: 230,  y: 65,  type: 'battery', minNight: 1 }, // Lobby
      { x: 560,  y: 48,  type: 'health',  minNight: 1 }, // Dining
      { x: 1200, y: 148,  type: 'keycard',  minNight: 1 }, // Stage — Special #1: Keycard

      // ─────────────────────────────────────────────────────────────────────
      // NIGHT 2 — +4 (total 7), adds Floor 2
      // ─────────────────────────────────────────────────────────────────────
      { x: 1850, y: 65,  type: 'vhs_tape', minNight: 2 }, // Arcade F1 — Special #2: VHS Tape
      { x: 2750, y: 65,  type: 'battery', minNight: 2 }, // PlayPlace F1
      { x: 750,  y: 440, type: 'battery', minNight: 2 }, // Backstage F2
      { x: 1600, y: 445, type: 'battery',  minNight: 2 }, // Break Room F2

      // ─────────────────────────────────────────────────────────────────────
      // NIGHT 3 — +5 (total 12), denser on F1 + F2
      // ─────────────────────────────────────────────────────────────────────
      { x: 3200, y: 65,  type: 'health',  minNight: 3 }, // Party A F1
      { x: 3900, y: 65,  type: 'battery', minNight: 3 }, // Party B F1
      { x: 880,  y: 440, type: 'pizza',    minNight: 3 }, // Backstage F2 — Special #3: Pizza Slice
      { x: 2900, y: 440, type: 'health',  minNight: 3 }, // Security F2
      { x: 4480, y: 440, type: 'health',   minNight: 3 }, // Storage F2

      // ─────────────────────────────────────────────────────────────────────
      // NIGHT 4 — +6 (total 18), unlocks Floor 3
      // ─────────────────────────────────────────────────────────────────────
      { x: 4450, y: 65,  type: 'health',  minNight: 4 }, // Party C F1
      { x: 400,  y: 860, type: 'fuse',     minNight: 4 }, // Workshop F3 — Special #4: Fuse
      { x: 1250, y: 860, type: 'health',  minNight: 4 }, // Maintenance F3
      { x: 2750, y: 860, type: 'battery', minNight: 4 }, // Server Room F3
      { x: 3750, y: 860, type: 'battery',  minNight: 4 }, // Observation F3
      { x: 4550, y: 860, type: 'lore',    minNight: 4 }, // Observation F3

      // ─────────────────────────────────────────────────────────────────────
      // NIGHT 5 — +7 (total 25), all floors denser
      // ─────────────────────────────────────────────────────────────────────
      { x: 700,  y: 65,  type: 'badge',    minNight: 5 }, // Stage F1 — Special #5: Staff Badge
      { x: 2050, y: 65,  type: 'battery', minNight: 5 }, // Arcade F1
      { x: 200,  y: 440, type: 'health',  minNight: 5 }, // Kitchen F2
      { x: 3350, y: 435, type: 'battery',  minNight: 5 }, // Manager F2
      { x: 600,  y: 860, type: 'battery', minNight: 5 }, // Workshop F3
      { x: 1800, y: 860, type: 'health',  minNight: 5 }, // Maintenance F3
      { x: 4200, y: 860, type: 'health',   minNight: 5 }, // Observation F3

      // ─────────────────────────────────────────────────────────────────────
      // NIGHT 6 — +8 (total 33), saturation across all rooms
      // ─────────────────────────────────────────────────────────────────────
      { x: 450,  y: 65,  type: 'lore',    minNight: 6 }, // Dining F1
      { x: 1600, y: 65,  type: 'battery',  minNight: 6 }, // Stage F1
      { x: 3600, y: 65,  type: 'health',  minNight: 6 }, // Party B F1
      { x: 1300, y: 445, type: 'battery', minNight: 6 }, // Break Room F2
      { x: 4050, y: 440, type: 'health',  minNight: 6 }, // Storage F2
      { x: 200,  y: 860, type: 'battery', minNight: 6 }, // Workshop F3
      { x: 2500, y: 860, type: 'health',   minNight: 6 }, // Server F3
      { x: 3950, y: 860, type: 'health',  minNight: 6 }, // Observation F3

      // ─────────────────────────────────────────────────────────────────────
      // NIGHT 7 — +9 (total 42), final night full coverage
      // ─────────────────────────────────────────────────────────────────────
      { x: 100,  y: 65,  type: 'health',  minNight: 7 }, // Lobby F1
      { x: 1400, y: 65,  type: 'battery', minNight: 7 }, // Stage F1
      { x: 2500, y: 65,  type: 'battery',  minNight: 7 }, // Arcade F1
      { x: 4650, y: 65,  type: 'lore',    minNight: 7 }, // Party C F1
      { x: 650,  y: 440, type: 'health',   minNight: 7 }, // Backstage F2
      { x: 2200, y: 440, type: 'battery', minNight: 7 }, // Break Room F2
      { x: 4600, y: 440, type: 'health',  minNight: 7 }, // Storage F2
      { x: 3600, y: 860, type: 'battery',  minNight: 7 }, // Server F3
      { x: 1050, y: 860, type: 'lore',    minNight: 7 }, // Workshop F3
    ];

    // Filter to collectibles available on this night
    const collectiblesData = allCollectibles.filter(c => c.minNight <= this.currentNight);

    const collectibles: Collectible[] = [];

    collectiblesData.forEach((data, index) => {
      const collectible = new Collectible(
        `collectible-${index}`,
        data.type,
        { x: data.x, y: data.y },
        { width: 20, height: 20 },
        25, // Value (25 health/battery, or 1 for lore/upgrade)
        true // Auto-collect when player gets close
      );

      collectible.initialize();

      // Build a recognizable 2D silhouette for each collectible type
      const glowColor = collectible.getGlowColor() || '#ffffff';

      const buildShape = (type: string): THREE.ShapeGeometry => {
        const s = new THREE.Shape();
        switch (type) {
          case 'battery': {
            // AA battery side-view: body + positive terminal nub on top
            s.moveTo(-5, -10); s.lineTo(5, -10);
            s.lineTo(5, 7);   s.lineTo(2, 7);
            s.lineTo(2, 11);  s.lineTo(-2, 11);
            s.lineTo(-2, 7);  s.lineTo(-5, 7);
            s.closePath();
            break;
          }
          case 'health': {
            // Medical plus / cross
            s.moveTo(-3, -10); s.lineTo(3, -10);
            s.lineTo(3, -3);   s.lineTo(10, -3);
            s.lineTo(10, 3);   s.lineTo(3, 3);
            s.lineTo(3, 10);   s.lineTo(-3, 10);
            s.lineTo(-3, 3);   s.lineTo(-10, 3);
            s.lineTo(-10, -3); s.lineTo(-3, -3);
            s.closePath();
            break;
          }
          case 'key': {
            // Key: oval head + rectangular shaft + two teeth
            s.moveTo(-1.5, -10); s.lineTo(1.5, -10);
            s.lineTo(1.5, 1);
            s.lineTo(4, 1);   s.lineTo(4, 4);
            s.lineTo(1.5, 4);
            s.lineTo(1.5, 6);  s.lineTo(4, 6);
            s.lineTo(4, 9);   s.lineTo(1.5, 9);
            s.lineTo(1.5, 11); s.lineTo(-1.5, 11);
            s.closePath();
            break;
          }
          case 'lore': {
            // Document / paper with dog-eared top-right corner
            s.moveTo(-8, -11); s.lineTo(5, -11);
            s.lineTo(9, -7);   s.lineTo(9, 11);
            s.lineTo(-8, 11);  s.closePath();
            // Folded corner triangle (punch out via hole path)
            const hole = new THREE.Path();
            hole.moveTo(5, -11); hole.lineTo(9, -7); hole.lineTo(5, -7); hole.closePath();
            s.holes.push(hole);
            break;
          }
          case 'taser': {
            // Lightning bolt (zig-zag)
            s.moveTo(2, 11);  s.lineTo(-4, 1);
            s.lineTo(0, 1);   s.lineTo(-2, -11);
            s.lineTo(4, -1);  s.lineTo(0, -1);
            s.closePath();
            break;
          }
          case 'keycard': {
            // Horizontal credit-card with chip notch cut from top-right corner
            s.moveTo(-10, -6); s.lineTo(7, -6);
            s.lineTo(10, -3); s.lineTo(10, 6);
            s.lineTo(-10, 6); s.closePath();
            // Magnetic stripe (horizontal bar hole)
            const stripe = new THREE.Path();
            stripe.moveTo(-9, 2); stripe.lineTo(8, 2);
            stripe.lineTo(8, 4); stripe.lineTo(-9, 4); stripe.closePath();
            s.holes.push(stripe);
            // Chip hole
            const chip = new THREE.Path();
            chip.moveTo(-7, -4); chip.lineTo(-2, -4);
            chip.lineTo(-2, 0);  chip.lineTo(-7, 0); chip.closePath();
            s.holes.push(chip);
            break;
          }
          case 'vhs_tape': {
            // Wide cassette body with two reel holes
            s.moveTo(-11, -7); s.lineTo(11, -7);
            s.lineTo(11, 7);   s.lineTo(5, 7);
            s.lineTo(5, 3);    s.lineTo(-5, 3);
            s.lineTo(-5, 7);   s.lineTo(-11, 7); s.closePath();
            // Left reel hole
            const reelL = new THREE.Path();
            reelL.absarc(-5.5, -1, 2.5, 0, Math.PI * 2, false);
            s.holes.push(reelL);
            // Right reel hole
            const reelR = new THREE.Path();
            reelR.absarc(5.5, -1, 2.5, 0, Math.PI * 2, false);
            s.holes.push(reelR);
            break;
          }
          case 'pizza': {
            // Triangle wedge (pizza slice) with crust arc at bottom
            s.moveTo(0, 11);
            s.lineTo(-8, -8); s.lineTo(8, -8);
            s.closePath();
            // Crust hole — arc at the wide end
            const crust = new THREE.Path();
            crust.absarc(0, -8, 3, 0, Math.PI, true);
            s.holes.push(crust);
            break;
          }
          case 'fuse': {
            // Cylindrical fuse: narrow body + wider metal end caps
            s.moveTo(-3, -11); s.lineTo(3, -11);
            s.lineTo(3, -8);   s.lineTo(5, -8);
            s.lineTo(5, -5);   s.lineTo(3, -5);
            s.lineTo(3, 5);    s.lineTo(5, 5);
            s.lineTo(5, 8);    s.lineTo(3, 8);
            s.lineTo(3, 11);   s.lineTo(-3, 11);
            s.lineTo(-3, 8);   s.lineTo(-5, 8);
            s.lineTo(-5, 5);   s.lineTo(-3, 5);
            s.lineTo(-3, -5);  s.lineTo(-5, -5);
            s.lineTo(-5, -8);  s.lineTo(-3, -8);
            s.closePath();
            // Glass window hole (shows wire inside)
            const window = new THREE.Path();
            window.moveTo(-1.5, -4); window.lineTo(1.5, -4);
            window.lineTo(1.5, 4);   window.lineTo(-1.5, 4); window.closePath();
            s.holes.push(window);
            break;
          }
          case 'badge': {
            // Portrait ID badge with lanyard hole at top + photo cutout
            s.moveTo(-8, -11); s.lineTo(8, -11);
            s.lineTo(8, 11);   s.lineTo(-8, 11); s.closePath();
            // Lanyard hole at top center
            const lanyardHole = new THREE.Path();
            lanyardHole.absarc(0, 9, 2, 0, Math.PI * 2, false);
            s.holes.push(lanyardHole);
            // Photo rectangle
            const photo = new THREE.Path();
            photo.moveTo(-5, 2); photo.lineTo(5, 2);
            photo.lineTo(5, 7);  photo.lineTo(-5, 7); photo.closePath();
            s.holes.push(photo);
            // Name line 1
            const name1 = new THREE.Path();
            name1.moveTo(-5, -2); name1.lineTo(5, -2);
            name1.lineTo(5, -1); name1.lineTo(-5, -1); name1.closePath();
            s.holes.push(name1);
            // Name line 2
            const name2 = new THREE.Path();
            name2.moveTo(-5, -5); name2.lineTo(3, -5);
            name2.lineTo(3, -4);  name2.lineTo(-5, -4); name2.closePath();
            s.holes.push(name2);
            break;
          }
          case 'upgrade': {
            // 5-pointed star
            const outerR = 10, innerR = 4, points = 5;
            for (let i = 0; i < points * 2; i++) {
              const r = i % 2 === 0 ? outerR : innerR;
              const angle = (i * Math.PI) / points - Math.PI / 2;
              const px = r * Math.cos(angle), py = r * Math.sin(angle);
              i === 0 ? s.moveTo(px, py) : s.lineTo(px, py);
            }
            s.closePath();
            break;
          }
          case 'weapon': {
            // Simple gun silhouette (top-down view: barrel left, grip bottom)
            s.moveTo(-10, 1); s.lineTo(7, 1);
            s.lineTo(7, -3);  s.lineTo(10, -3);
            s.lineTo(10, 3);  s.lineTo(3, 3);
            s.lineTo(3, 9);   s.lineTo(-2, 9);
            s.lineTo(-2, 3);  s.lineTo(-10, 3);
            s.closePath();
            break;
          }
          default: {
            // Fallback: diamond
            s.moveTo(0, -10); s.lineTo(9, 0);
            s.lineTo(0, 10);  s.lineTo(-9, 0);
            s.closePath();
          }
        }
        return new THREE.ShapeGeometry(s);
      };

      // Glow layer: slightly larger, 30% opacity same color
      const glowGeo = buildShape(data.type);
      glowGeo.scale(1.35, 1.35, 1);
      const glowMat = new THREE.MeshBasicMaterial({
        color: glowColor, transparent: true, opacity: 0.3,
      });
      const glowMesh = new THREE.Mesh(glowGeo, glowMat);
      glowMesh.position.set(data.x, data.y, 1.4);
      glowMesh.renderOrder = 6;
      this.scene.add(glowMesh);

      // Main icon
      const geometry = buildShape(data.type);
      const material = new THREE.MeshBasicMaterial({ color: glowColor });
      const mesh = new THREE.Mesh(geometry, material);
      // z=1.5 puts collectibles in FRONT of platforms (z=0) and containers (z=0.5)
      mesh.position.set(data.x, data.y, 1.5);
      mesh.renderOrder = 7; // above platforms(default) below player(10)
      this.scene.add(mesh);
      this.collectibleMeshes.push(mesh);

      // Keep glow mesh in sync during updates
      (mesh as any).__glowMesh = glowMesh;


      collectibles.push(collectible);
    });

    console.log(`Created ${collectibles.length} collectibles`);
    return collectibles;
  }

  /**
   * Create hidden searchable containers (lockers, desks, boxes, cabinets).
   * Items are night-gated: container.itemId is only granted if currentNight >= minNight.
   *
   * KEY CHAIN:
   *   Night 1 → employee_key (Lobby front desk) → opens all F2 rooms
   *   Night 2 → manager_key (Security filing cabinet) → opens Manager's Office
   *   Night 3 → restricted_key (Manager's safe) → opens all F3 rooms
   */
  private createHiddenContainers(): void {
    // shape: 'locker'(28×50) | 'desk'(50×18) | 'cabinet'(22×38) | 'box'(28×28) | 'safe'(18×22)
    const containerDefs: Array<{
      id: string; x: number; y: number;
      label: string; shape: 'locker' | 'desk' | 'cabinet' | 'box' | 'safe';
      itemId: string | null; itemName: string; minNight: number;
    }> = [
      // ── FLOOR 1 (public, minNight=1) ──────────────────────────────────────
      { id: 'c-lobby-desk',    x: -55,  y: 65,  shape: 'desk',    label: 'Search front desk',       itemId: 'employee_key',  itemName: 'Employee Key',           minNight: 1 },
      { id: 'c-dining-table',  x: 415,  y: 98,  shape: 'box',     label: 'Search table drawer',     itemId: 'battery_pack',  itemName: 'Battery Pack',           minNight: 1 },
      { id: 'c-stage-curtain', x: 905,  y: 118, shape: 'box',     label: 'Search behind curtain',   itemId: 'newspaper_1',   itemName: 'Newspaper (Lore)',        minNight: 1 },
      { id: 'c-arcade-cab',    x: 1750, y: 100, shape: 'cabinet', label: 'Search prize cabinet',    itemId: 'battery_pack',  itemName: 'Battery Pack',           minNight: 1 },
      { id: 'c-lobby-couch',   x: 130,  y: 60,  shape: 'box',     label: 'Search under couch',      itemId: 'health_kit',    itemName: 'Health Kit',             minNight: 1 },
      { id: 'c-playplace-box', x: 2650, y: 70,  shape: 'box',     label: 'Search toy chest',        itemId: 'battery_pack',  itemName: 'Battery Pack',           minNight: 1 },
      { id: 'c-partyb-shelf',  x: 3800, y: 60,  shape: 'box',     label: 'Search supply shelf',     itemId: 'health_kit',    itemName: 'Health Kit',             minNight: 1 },
      { id: 'c-partyc-box',    x: 4500, y: 70,  shape: 'box',     label: 'Search storage box',      itemId: 'battery_pack',  itemName: 'Battery Pack',           minNight: 1 },

      // ── FLOOR 2 employee rooms (need employee_key to enter, minNight=2) ───
      // y positions: floor surface y=415 + half item height
      { id: 'c-kitchen-cab',   x: 200,  y: 434, shape: 'cabinet', label: 'Search kitchen cabinet',  itemId: 'health_kit',    itemName: 'Health Kit',             minNight: 2 },
      { id: 'c-backstage-box', x: 720,  y: 429, shape: 'box',     label: 'Search prop box',         itemId: 'battery_pack',  itemName: 'Battery Pack',           minNight: 2 },
      { id: 'c-breakroom-lok', x: 1700, y: 440, shape: 'locker',  label: 'Search locker',           itemId: 'audio_log_1',   itemName: 'Audio Log (Lore)',        minNight: 2 },
      { id: 'c-security-fil',  x: 2750, y: 434, shape: 'cabinet', label: 'Search filing cabinet',   itemId: 'manager_key',   itemName: "Manager's Key",          minNight: 2 },
      { id: 'c-storage-shelf', x: 4250, y: 429, shape: 'box',     label: 'Search storage shelf',    itemId: 'health_kit',    itemName: 'Health Kit',             minNight: 2 },

      // ── Manager's Office (needs manager_key, minNight=3) ──────────────────
      { id: 'c-manager-desk',  x: 3520, y: 424, shape: 'desk',    label: 'Search manager\'s desk',  itemId: 'employee_id_1', itemName: 'Employee Badge (Lore)',   minNight: 3 },
      { id: 'c-manager-safe',  x: 3680, y: 426, shape: 'safe',    label: 'Open safe',               itemId: 'restricted_key',itemName: 'Restricted Area Key',    minNight: 3 },

      // ── Floor 3 restricted rooms (needs restricted_key, minNight=4) ───────
      { id: 'c-animat-cab',    x: 300,  y: 860, shape: 'cabinet', label: 'Search workshop cabinet', itemId: 'battery_pack',  itemName: 'Battery Pack',           minNight: 4 },
      { id: 'c-maint-box',     x: 1500, y: 860, shape: 'box',     label: 'Search maintenance box',  itemId: 'health_kit',    itemName: 'Health Kit',             minNight: 4 },
      { id: 'c-server-rack',   x: 3000, y: 855, shape: 'cabinet', label: 'Search server rack',      itemId: 'battery_pack',  itemName: 'Battery Pack',           minNight: 4 },
      { id: 'c-obs-locker',    x: 4200, y: 860, shape: 'locker',  label: 'Search locker',           itemId: 'health_kit',    itemName: 'Health Kit',             minNight: 4 },
    ];

    const shapeSize: Record<string, [number, number]> = {
      locker: [28, 50], desk: [50, 18], cabinet: [22, 38], box: [28, 28], safe: [18, 22],
    };
    const shapeColor: Record<string, number> = {
      locker: 0x778899, desk: 0x7B5914, cabinet: 0x445566, box: 0x4B2A19, safe: 0x334455,
    };

    for (const def of containerDefs) {
      const size = shapeSize[def.shape] ?? [28, 28];
      const w = size[0], h = size[1];
      const color = shapeColor[def.shape] ?? 0x555555;
      const geo = new THREE.PlaneGeometry(w, h);
      const mat = new THREE.MeshBasicMaterial({ color });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(def.x, def.y, 2);
      mesh.visible = false;
      this.scene.add(mesh);

      // Item is only available if currentNight meets requirement
      const activeItemId = this.currentNight >= def.minNight ? def.itemId : null;

      this.hiddenContainers.push({
        id: def.id, x: def.x, y: def.y,
        label: def.label,
        itemId: activeItemId,
        itemName: def.itemName,
        minNight: def.minNight,
        searched: false,
        mesh,
      });
    }

    console.log(`Created ${this.hiddenContainers.length} hidden containers`);
  }

  /**
   * Create door entities - DIRECTLY ON PLATFORMS
   * Door Y = Platform Y + 40 (door is 60px tall, so bottom touches platform top)
   */
  private createDoors(): Door[] {
    const doorsData: Array<{ x: number; y: number; locked: boolean; label: string; backLabel: string; clue: string; roomX: number; roomY: number; key?: string; doorStyle?: string }> = [
      // FLOOR 1 - Public doors
      // backLabel = what the player sees when inside, looking back out
      { x: -210, y: 50, locked: false, label: 'Front Entrance', backLabel: 'Outside',       clue: 'Welcome to Khan\'s Pizza Palace!', roomX: 20,   roomY: 50 },
      { x: 300,  y: 50, locked: false, label: 'Dining Area',    backLabel: 'Lobby',          clue: 'Best pizza in town!',              roomX: 320,  roomY: 50 },
      { x: 800,  y: 50, locked: false, label: 'Show Stage',     backLabel: 'Dining Area',    clue: 'Come see the amazing animatronics!',roomX: 1070, roomY: 50 },
      { x: 1400, y: 50, locked: false, label: 'Arcade',         backLabel: 'Show Stage',     clue: 'Play games and win prizes!',        roomX: 1770, roomY: 50 },
      { x: 2488, y: 50, locked: false, label: 'Play Place',     backLabel: 'Arcade',         clue: 'Fun for the whole family!',         roomX: 2620, roomY: 50 },
      { x: 2900, y: 50, locked: false, label: 'Party Room A',   backLabel: 'Play Place',     clue: 'Book your birthday party here!',    roomX: 3170, roomY: 50 },
      { x: 3500, y: 50, locked: false, label: 'Party Room B',   backLabel: 'Party Room A',   clue: 'More party fun!',                   roomX: 3770, roomY: 50 },
      { x: 4100, y: 50, locked: false, label: 'Party Room C',   backLabel: 'Party Room B',   clue: 'Even more party fun!',              roomX: 4420, roomY: 50 },

      // FLOOR 2 - Employee Only (employee_key), Manager's Office (manager_key)
      // All floor 2 doors: y=445 (floor surface at y=415, door height=60, center=415+30)
      { x: 290,  y: 445, locked: true, label: 'Kitchen',          backLabel: 'Employee Hallway', clue: 'Employee Key Required', roomX: 500,  roomY: 450, key: 'employee_key' },
      { x: 500,  y: 445, locked: true, label: 'Backstage',        backLabel: 'Kitchen',          clue: 'Employee Key Required', roomX: 800,  roomY: 450, key: 'employee_key' },
      { x: 1100, y: 445, locked: true, label: 'Break Room',       backLabel: 'Employee Hallway', clue: 'Employee Key Required', roomX: 1650, roomY: 450, key: 'employee_key' },
      { x: 2600, y: 445, locked: true, label: 'Security Room',    backLabel: 'Employee Hallway', clue: 'Employee Key Required', roomX: 2850, roomY: 450, key: 'employee_key', doorStyle: 'security' },
      { x: 3200, y: 445, locked: true, label: 'Manager\'s Office',backLabel: 'Employee Hallway', clue: 'Manager\'s Key Required', roomX: 3600, roomY: 450, key: 'manager_key' },
      { x: 4000, y: 445, locked: true, label: 'Storage Room',     backLabel: 'Employee Hallway', clue: 'Employee Key Required', roomX: 4300, roomY: 450, key: 'employee_key' },

      // FLOOR 3 - Restricted (restricted_key)
      { x: 400,  y: 857, locked: true, label: 'Animatronics Workshop', backLabel: 'Restricted Corridor', clue: 'Restricted Key Required', roomX: 400,  roomY: 850, key: 'restricted_key' },
      { x: 1724, y: 850, locked: true, label: 'Maintenance',           backLabel: 'Restricted Corridor', clue: 'Restricted Key Required', roomX: 1600, roomY: 850, key: 'restricted_key' },
      { x: 3600, y: 850, locked: true, label: 'Server Room',           backLabel: 'Restricted Corridor', clue: 'Restricted Key Required', roomX: 3000, roomY: 850, key: 'restricted_key' },
    ];

    const doors: Door[] = [];

    doorsData.forEach((data, index) => {
      const door = new Door({
        id: `door-${index}`,
        position: { x: data.x, y: data.y },
        size: { width: 40, height: 60 },
        locked: data.locked,
        targetLevel: null,
        targetSpawn: null,
        requiredKey: data.key ?? null,
        doorStyle: data.doorStyle,
      });

      // Store the clue message and room teleport position as metadata on the door
      (door as any).clueMessage = data.clue;
      (door as any).doorLabel = data.label;
      (door as any).doorBackLabel = data.backLabel;
      (door as any).roomTeleportX = data.roomX;
      (door as any).roomTeleportY = data.roomY;

      // Add door mesh to scene
      const mesh = door.getMesh();
      if (mesh) {
        this.scene.add(mesh);
      }

      doors.push(door);
    });

    console.log(`Created ${doors.length} doors`);
    return doors;
  }

  /**
   * Create vent entities - Pizza place vent system for shortcuts
   */
  private createVents(): Vent[] {
    const ventsData = [
      // Floor 1: Dining → Show Stage
      { entranceX: 750, entranceY: 150, exitX: 850, exitY: 150 },
      // Floor 1: Arcade → Play Place (through elevator shaft walls)
      { entranceX: 2120, entranceY: 155, exitX: 2500, exitY: 150 },
      // Floor 2: Kitchen → Backstage
      { entranceX: 450, entranceY: 650, exitX: 550, exitY: 650 },
      // Floor 2: Backstage → Break Room
      { entranceX: 1050, entranceY: 550, exitX: 1150, exitY: 550 },
      // Floor 2: Security → Manager
      { entranceX: 3150, entranceY: 550, exitX: 3250, exitY: 550 },
      // Floor 3: Animatronics → Maintenance
      { entranceX: 950, entranceY: 920, exitX: 1050, exitY: 920 },
      // Floor 3: Server Room → Observation
      { entranceX: 3550, entranceY: 920, exitX: 3650, exitY: 920 },
    ];

    const vents: Vent[] = [];

    ventsData.forEach((data, index) => {
      const vent = new Vent({
        id: `vent-${index}`,
        entrancePosition: { x: data.entranceX, y: data.entranceY },
        exitPosition: { x: data.exitX, y: data.exitY },
        size: { width: 50, height: 30 },
        requiresCrouch: true,
      });

      // Add vent meshes to scene
      const meshes = vent.getMeshes();
      if (meshes.entrance) this.scene.add(meshes.entrance);
      if (meshes.exit) this.scene.add(meshes.exit);

      vents.push(vent);
    });

    console.log(`Created ${vents.length} vents`);
    return vents;
  }

  /**
   * Create elevator entity - SINGLE CENTER ELEVATOR with doors
   * Located in the center of the building (x=2300) spanning all 3 floors
   * Press E/button to cycle: F1 → F2 → F3 → F1
   */
  private createElevators(): Elevator[] {
    const elevator = new Elevator({
      id: 'elevator-center',
      position: { x: 2300, y: 90 },   // Center of building, starts at F1 (raised above ground)
      size: { width: 160, height: 120 }, // Wide enough for player + doors visible
      topPosition: 890,                 // Floor 3 surface
      bottomPosition: 90,               // Floor 1 surface (elevated so bottom edge clears ground)
      speed: 200,                        // pixels/second
      waitTime: 3,
      startsAt: 'bottom',
      floorStops: [90, 490, 890],       // F1, F2, F3 floor surfaces (raised by 60)
    });

    // Register elevator with physics and collision systems
    elevator.initialize();

    // Add elevator's mesh to scene
    const mesh = elevator.getMesh();
    if (mesh) {
      this.scene.add(mesh);
    }

    console.log('Created center elevator with 3 floor stops and doors');
    return [elevator];
  }

  /**
   * Create shaft gate entities - solid blockers that fill shaft wall openings and
   * floor gaps when the elevator is not present at that floor.
   * Elevator starts at F1 (index 0), so F2 and F3 gates start registered (blocking).
   */
  private createShaftGates(): void {
    // Each gate: which floor it belongs to, its world position and size
    const gateConfigs = [
      // F2 gates (floorIndex=1): wall openings only (no floor bar - player must descend through)
      { id: 'shaft-gate-f2-left',  floorIndex: 1, x: 2190, y: 475, w: 20, h: 120 },
      { id: 'shaft-gate-f2-right', floorIndex: 1, x: 2410, y: 475, w: 20, h: 120 },
      // F3 gates (floorIndex=2): wall openings only
      { id: 'shaft-gate-f3-left',  floorIndex: 2, x: 2190, y: 875, w: 20, h: 120 },
      { id: 'shaft-gate-f3-right', floorIndex: 2, x: 2410, y: 875, w: 20, h: 120 },
    ];

    for (const cfg of gateConfigs) {
      const entity: Entity = {
        id: cfg.id,
        type: 'static',
        position: { x: cfg.x, y: cfg.y },
        velocity: { x: 0, y: 0 },
        size: { width: cfg.w, height: cfg.h },
        rotation: 0,
        active: true,
        destroyed: false,
      };

      // Visual: dark mesh matching shaft wall color
      const geo = new THREE.PlaneGeometry(cfg.w, cfg.h);
      const mat = new THREE.MeshBasicMaterial({ color: 0x2a2a3a });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(cfg.x, cfg.y, 1);
      this.scene.add(mesh);

      // Elevator starts at F1, so F2/F3 gates start registered (blocking)
      this.physicsEngine.addEntity(entity as any, {
        mass: 0, gravity: 0, friction: 0.8, restitution: 0,
        isKinematic: true, isGrounded: false, canJump: false,
      });
      this.collisionSystem.addCollider(entity as any, {
        type: 'box', isTrigger: false, layer: 'static',
      });

      this.shaftGates.push({ floorIndex: cfg.floorIndex, entity, mesh, isRegistered: true });
    }
  }

  /**
   * Create call button visuals - one on each side of the shaft opening per floor.
   * Player presses E when near a button to call the elevator to that floor.
   */
  private createCallButtons(): void {
    const buttonDefs = [
      { floorIndex: 0, x: 2155, y: 100 },  // F1 left
      { floorIndex: 1, x: 2155, y: 490 },  // F2 left
      { floorIndex: 1, x: 2445, y: 490 },  // F2 right
      { floorIndex: 2, x: 2155, y: 890 },  // F3 left
      { floorIndex: 2, x: 2445, y: 890 },  // F3 right
    ];

    for (const def of buttonDefs) {
      const group = new THREE.Group();
      group.position.set(def.x, def.y, 2);

      // Panel background
      const panelMesh = new THREE.Mesh(
        new THREE.PlaneGeometry(24, 36),
        new THREE.MeshBasicMaterial({ color: 0x1a1a2a })
      );
      group.add(panelMesh);

      // Button face
      const btnMesh = new THREE.Mesh(
        new THREE.CircleGeometry(8, 12),
        new THREE.MeshBasicMaterial({ color: 0x444455 })
      );
      btnMesh.position.set(0, 4, 1);
      group.add(btnMesh);

      // Status light (red = elevator away, green = elevator here)
      const lightMesh = new THREE.Mesh(
        new THREE.CircleGeometry(4, 8),
        new THREE.MeshBasicMaterial({ color: 0x553333 })
      );
      lightMesh.position.set(0, -10, 1);
      group.add(lightMesh);

      this.scene.add(group);
      this.callButtons.push({ floorIndex: def.floorIndex, x: def.x, y: def.y, mesh: group, lightMesh });
    }
  }

  /**
   * Update shaft gates and call button lights based on elevator position.
   * Called every frame from update().
   */
  private updateShaftGates(): void {
    const elevator = this.elevators[0];
    if (!elevator) return;

    const elevAtF2 = elevator.isIdleAtFloor(1);
    const elevAtF3 = elevator.isIdleAtFloor(2);

    for (const gate of this.shaftGates) {
      const shouldBlock = gate.floorIndex === 1 ? !elevAtF2 : !elevAtF3;

      if (shouldBlock && !gate.isRegistered) {
        this.physicsEngine.addEntity(gate.entity as any, {
          mass: 0, gravity: 0, friction: 0.8, restitution: 0,
          isKinematic: true, isGrounded: false, canJump: false,
        });
        this.collisionSystem.addCollider(gate.entity as any, {
          type: 'box', isTrigger: false, layer: 'static',
        });
        gate.isRegistered = true;
        gate.mesh.visible = true;
      } else if (!shouldBlock && gate.isRegistered) {
        this.physicsEngine.removeEntity(gate.entity.id);
        this.collisionSystem.removeCollider(gate.entity.id);
        gate.isRegistered = false;
        gate.mesh.visible = false;
      }
    }

    // Update button status lights: green = elevator here, dark red = away
    // Also show a proximity hint when player is near a call button
    let nearBtn: typeof this.callButtons[0] | null = null;
    for (const btn of this.callButtons) {
      const here = elevator.isIdleAtFloor(btn.floorIndex);
      (btn.lightMesh.material as THREE.MeshBasicMaterial).color.setHex(here ? 0x00cc00 : 0x553333);
      const dx = Math.abs(this.player.position.x - btn.x);
      const dy = Math.abs(this.player.position.y - btn.y);
      if (dx < 80 && dy < 80) nearBtn = btn;
    }

    // Show/hide call button hint (uses a dedicated element so it doesn't conflict with interaction prompt)
    let hintEl = document.getElementById('call-button-hint');
    if (!hintEl) {
      hintEl = document.createElement('div');
      hintEl.id = 'call-button-hint';
      hintEl.style.cssText = 'position:fixed;bottom:145px;left:50%;transform:translateX(-50%);background:rgba(0,0,0,0.85);color:#ffdd44;padding:8px 20px;border-radius:6px;border:1px solid #ffdd44;font-size:14px;font-family:monospace;font-weight:bold;z-index:8999;pointer-events:none;display:none;text-align:center';
      document.body.appendChild(hintEl);
    }
    // Container hint takes priority over call button hint
    let nearContainer: typeof this.hiddenContainers[0] | null = null;
    for (const c of this.hiddenContainers) {
      if (!c.searched) {
        const dx = Math.abs(this.player.position.x - c.x);
        const dy = Math.abs(this.player.position.y - c.y);
        if (dx < 60 && dy < 60) { nearContainer = c; break; }
      }
    }

    if (nearContainer) {
      hintEl.textContent = `[E] ${nearContainer.label}`;
      hintEl.style.display = 'block';
    } else if (nearBtn) {
      hintEl.textContent = `[E] Call elevator to Floor ${nearBtn.floorIndex + 1}`;
      hintEl.style.display = 'block';
    } else {
      hintEl.style.display = 'none';
    }
  }

  /**
   * Create hiding spot entities
   */
  private createHidingSpots(): HidingSpot[] {
    const hidingSpotsData = [
      // FLOOR 1: Under dining tables
      { x: 400, y: 55, type: 'under_table' as const },
      { x: 550, y: 55, type: 'under_table' as const },
      { x: 700, y: 55, type: 'under_table' as const },
      // FLOOR 1: Behind service counter
      { x: 620, y: 50, type: 'closet' as const },
      // FLOOR 1: Behind arcade cabinets
      { x: 1990, y: 45, type: 'locker' as const },
      // FLOOR 1: In ball pit
      //{ x: 2500, y: 35, type: 'under_table' as const },
      // FLOOR 1: Under party tables
      { x: 3000, y: 50, type: 'under_table' as const },
      { x: 3660, y: 50, type: 'under_table' as const },
      { x: 4270, y: 50, type: 'under_table' as const },
      // FLOOR 2: Backstage closet
      { x: 740, y: 440, type: 'closet' as const },
      // FLOOR 2: Break room lockers
      { x: 1824, y: 440, type: 'locker' as const },
      // FLOOR 2: Security closet
      { x: 2978, y: 440, type: 'closet' as const },
      // FLOOR 3: Animatronics workshop
      { x: 200, y: 840, type: 'closet' as const },
      // FLOOR 3: Maintenance corridor
      { x: 1480, y: 840, type: 'locker' as const },
      // FLOOR 3: Observation deck
      { x: 4200, y: 840, type: 'closet' as const },
    ];

    const hidingSpots: HidingSpot[] = [];

    hidingSpotsData.forEach((data, index) => {
      const hidingSpot = new HidingSpot({
        id: `hiding-spot-${index}`,
        position: { x: data.x, y: data.y },
        size: { width: 35, height: 50 },
        hideTime: 15,
        type: data.type,
      });

      // Add hiding spot's mesh to scene
      const mesh = hidingSpot.getMesh();
      if (mesh) {
        this.scene.add(mesh);
      }

      hidingSpots.push(hidingSpot);
    });

    console.log(`Created ${hidingSpots.length} hiding spots`);
    return hidingSpots;
  }

  // ─────────────────────────────────────────────
  // SECURITY CAMERA UI (FNAF-style monitor)
  // ─────────────────────────────────────────────

  /**
   * Open the FNAF-style security camera overlay
   */
  private openCameraUI(): void {
    if (this.cameraUIOpen) return;
    this.cameraUIOpen = true;

    // Inject CSS once
    if (!document.getElementById('cam-ui-style')) {
      const style = document.createElement('style');
      style.id = 'cam-ui-style';
      style.textContent = `
        #camera-ui {
          position: fixed; inset: 0; z-index: 9000;
          background: rgba(0,0,0,0.92);
          display: flex; align-items: center; justify-content: center;
          font-family: 'Courier New', monospace;
        }
        #cam-monitor {
          width: 860px; max-width: 98vw;
          border: 3px solid #00ff41;
          background: #050f05;
          color: #00ff41;
          box-shadow: 0 0 40px #00ff4188, inset 0 0 20px #00ff4110;
        }
        #cam-header {
          background: #001800;
          border-bottom: 2px solid #00ff41;
          padding: 8px 16px;
          display: flex; justify-content: space-between; align-items: center;
          font-size: 13px; letter-spacing: 2px; text-transform: uppercase;
        }
        #cam-body {
          display: flex; height: 420px;
        }
        #cam-list {
          width: 210px; border-right: 2px solid #00ff4155;
          overflow-y: auto; padding: 6px 0;
          background: #020a02;
        }
        .cam-item {
          padding: 6px 12px; cursor: pointer; font-size: 11px;
          letter-spacing: 1px; border-left: 3px solid transparent;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
          transition: background 0.1s;
          display: flex; justify-content: space-between; align-items: center;
        }
        .cam-item:hover { background: #001800; }
        .cam-item.selected { border-left-color: #00ff41; background: #001f00; color: #66ff66; }
        .cam-item .motion-tag {
          font-size: 9px; color: #ff4400; animation: blink 0.5s step-end infinite;
          display: none; margin-left: 4px; flex-shrink: 0;
        }
        .cam-item .motion-tag.visible { display: inline; }
        #cam-main { flex: 1; display: flex; flex-direction: column; }
        #cam-label-bar {
          background: #001800; border-bottom: 1px solid #00ff4155;
          padding: 6px 14px; font-size: 12px; letter-spacing: 2px;
          display: flex; justify-content: space-between;
        }
        #cam-feed {
          flex: 1; position: relative; overflow: hidden;
          background: #020a02;
          display: flex; align-items: center; justify-content: center;
        }
        #cam-feed-content {
          width: 100%; height: 100%;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          position: relative;
        }
        #cam-scanlines {
          position: absolute; inset: 0; pointer-events: none;
          background: repeating-linear-gradient(
            0deg, transparent, transparent 2px, rgba(0,0,0,0.18) 2px, rgba(0,0,0,0.18) 4px
          );
        }
        #cam-room-label {
          font-size: 22px; letter-spacing: 4px; opacity: 0.7;
          text-align: center; text-transform: uppercase;
        }
        #cam-corner-tl {
          position: absolute; top: 8px; left: 10px;
          font-size: 10px; opacity: 0.6;
        }
        #cam-corner-tr {
          position: absolute; top: 8px; right: 10px;
          font-size: 10px; opacity: 0.6;
        }
        #cam-glitch {
          position: absolute; inset: 0; pointer-events: none; display: none;
          background: repeating-linear-gradient(
            0deg,
            rgba(255,68,0,0.08) 0px, transparent 2px,
            transparent 4px, rgba(0,255,65,0.05) 6px
          );
          animation: glitch-anim 0.12s steps(1) infinite;
        }
        #cam-glitch.active { display: block; }
        #cam-static {
          position: absolute; inset: 0; pointer-events: none; display: none;
          opacity: 0.35;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)' opacity='1'/%3E%3C/svg%3E");
          background-size: 200px 200px;
          mix-blend-mode: screen;
          animation: static-shift 0.08s steps(1) infinite;
        }
        #cam-static.active { display: block; }
        #cam-motion-alert {
          position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
          font-size: 28px; color: #ff4400; letter-spacing: 6px; font-weight: bold;
          display: none; text-shadow: 0 0 20px #ff4400;
          animation: blink 0.4s step-end infinite;
        }
        #cam-motion-alert.active { display: block; }
        #cam-footer {
          background: #001800; border-top: 2px solid #00ff41;
          padding: 6px 16px; font-size: 10px; letter-spacing: 1px;
          display: flex; justify-content: space-between;
          opacity: 0.8;
        }
        @keyframes blink { 50% { opacity: 0; } }
        @keyframes glitch-anim {
          0%  { clip-path: inset(20% 0 50% 0); transform: translateX(-4px); }
          25% { clip-path: inset(60% 0 10% 0); transform: translateX(4px);  }
          50% { clip-path: inset(10% 0 70% 0); transform: translateX(-2px); }
          75% { clip-path: inset(40% 0 30% 0); transform: translateX(3px);  }
          100%{ clip-path: none; transform: none; }
        }
        @keyframes static-shift {
          0%   { background-position: 0px 0px;    }
          33%  { background-position: -80px 40px;  }
          66%  { background-position: 60px -30px;  }
          100% { background-position: -20px 60px;  }
        }
      `;
      document.head.appendChild(style);
    }

    // Build the UI
    const ui = document.createElement('div');
    ui.id = 'camera-ui';

    const names = TestScene.CAMERA_NAMES;
    const labels = TestScene.CAMERA_LABELS;
    const idx = this.selectedCameraIndex;

    const listItems = names.map((name, i) => `
      <div class="cam-item${i === idx ? ' selected' : ''}" data-idx="${i}">
        <span>${labels[i]} | ${name}</span>
        <span class="motion-tag" id="motion-tag-${i}">!!!</span>
      </div>`).join('');

    ui.innerHTML = `
      <div id="cam-monitor">
        <div id="cam-header">
          <span>KHAN'S PIZZA PALACE — SECURITY SYSTEM</span>
          <span>NIGHT ${this.currentNight} &nbsp;|&nbsp; <span id="cam-clock">12:00 AM</span></span>
        </div>
        <div id="cam-body">
          <div id="cam-list">${listItems}</div>
          <div id="cam-main">
            <div id="cam-label-bar">
              <span id="cam-label">${labels[idx]} — ${names[idx]}</span>
              <span id="cam-status" style="color:#00ff41">● LIVE</span>
            </div>
            <div id="cam-feed">
              <div id="cam-feed-content">
                <div id="cam-corner-tl">${labels[idx]}</div>
                <div id="cam-corner-tr" id="cam-time-inner">REC ●</div>
                <div id="cam-room-label">${names[idx]}</div>
                <div id="cam-scanlines"></div>
                <div id="cam-glitch"></div>
                <div id="cam-static"></div>
                <div id="cam-motion-alert">MOTION DETECTED</div>
              </div>
            </div>
          </div>
        </div>
        <div id="cam-footer">
          <span>[E] / [ESC] Close</span>
          <span>[← →] Switch Camera</span>
          <span>CAMS: ${names.length} ACTIVE</span>
        </div>
      </div>`;

    document.body.appendChild(ui);
    this.cameraUIElement = ui;

    // Click on list items to select camera
    ui.querySelectorAll('.cam-item').forEach(el => {
      el.addEventListener('click', () => {
        const i = parseInt((el as HTMLElement).dataset.idx ?? '0', 10);
        this.selectedCameraIndex = i;
        this.refreshCameraSelection();
      });
    });

    // Keyboard: arrow keys to switch, Escape/E to close
    this.cameraUIKeyHandler = (e: KeyboardEvent) => {
      if (e.code === 'ArrowLeft' || e.code === 'KeyA') {
        this.selectedCameraIndex = (this.selectedCameraIndex - 1 + TestScene.CAMERA_NAMES.length) % TestScene.CAMERA_NAMES.length;
        this.refreshCameraSelection();
        e.stopImmediatePropagation();
      } else if (e.code === 'ArrowRight' || e.code === 'KeyD') {
        this.selectedCameraIndex = (this.selectedCameraIndex + 1) % TestScene.CAMERA_NAMES.length;
        this.refreshCameraSelection();
        e.stopImmediatePropagation();
      } else if (e.code === 'Escape' || e.code === 'KeyE') {
        this.closeCameraUI();
        e.stopImmediatePropagation();
      }
    };
    window.addEventListener('keydown', this.cameraUIKeyHandler, true); // capture phase to intercept movement keys
  }

  /**
   * Refresh camera list selection highlight + main feed label
   */
  private refreshCameraSelection(): void {
    if (!this.cameraUIElement) return;
    const names = TestScene.CAMERA_NAMES;
    const labels = TestScene.CAMERA_LABELS;
    const idx = this.selectedCameraIndex;

    // Update list selection
    this.cameraUIElement.querySelectorAll('.cam-item').forEach((el, i) => {
      el.classList.toggle('selected', i === idx);
    });

    // Update main feed label
    const labelEl = this.cameraUIElement.querySelector('#cam-label');
    if (labelEl) labelEl.textContent = `${labels[idx]} — ${names[idx]}`;
    const cornerEl = this.cameraUIElement.querySelector('#cam-corner-tl');
    if (cornerEl) cornerEl.textContent = labels[idx];
    const roomEl = this.cameraUIElement.querySelector('#cam-room-label');
    if (roomEl) roomEl.textContent = names[idx];
  }

  /**
   * Close the security camera UI
   */
  private closeCameraUI(): void {
    if (!this.cameraUIOpen) return;
    this.cameraUIOpen = false;

    if (this.cameraUIElement) {
      document.body.removeChild(this.cameraUIElement);
      this.cameraUIElement = null;
    }
    if (this.cameraUIKeyHandler) {
      window.removeEventListener('keydown', this.cameraUIKeyHandler, true);
      this.cameraUIKeyHandler = null;
    }
  }

  /**
   * Update camera UI glitch/motion effects each frame (called from update())
   */
  private updateCameraUI(): void {
    if (!this.cameraUIOpen || !this.cameraUIElement) return;

    const camData = this.securityCameras;

    // For each camera, check if any robot is within its viewDistance
    const motionFlags: boolean[] = camData.map(cam => {
      return this.robots.some(robot => {
        const dx = robot.position.x - cam.position.x;
        const dy = robot.position.y - cam.position.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        return dist < (cam as any).viewDistance ?? 300;
      });
    });

    // Update motion tags in the list
    motionFlags.forEach((hasMotion, i) => {
      const tag = this.cameraUIElement!.querySelector(`#motion-tag-${i}`);
      if (tag) tag.classList.toggle('visible', hasMotion);
    });

    // Update main feed glitch for selected camera
    const selectedMotion = motionFlags[this.selectedCameraIndex] ?? false;
    const glitchEl = this.cameraUIElement.querySelector('#cam-glitch');
    const staticEl = this.cameraUIElement.querySelector('#cam-static');
    const alertEl  = this.cameraUIElement.querySelector('#cam-motion-alert');
    const statusEl = this.cameraUIElement.querySelector('#cam-status');

    if (glitchEl) glitchEl.classList.toggle('active', selectedMotion);
    if (staticEl) staticEl.classList.toggle('active', selectedMotion);
    if (alertEl)  alertEl.classList.toggle('active', selectedMotion);
    if (statusEl) {
      (statusEl as HTMLElement).style.color = selectedMotion ? '#ff4400' : '#00ff41';
      statusEl.textContent = selectedMotion ? '! MOTION' : '● LIVE';
    }
  }

  /**
   * Create security camera entities
   */
  private createSecurityCameras(): SecurityCamera[] {
    const camerasData = [
      // FLOOR 1 cameras (near ceiling y≈380)
      { x: 50, y: 380, viewAngle: 80, viewDistance: 300 },      // Lobby
      { x: 550, y: 380, viewAngle: 100, viewDistance: 350 },    // Dining
      { x: 1100, y: 380, viewAngle: 90, viewDistance: 320 },    // Stage
      { x: 1800, y: 380, viewAngle: 80, viewDistance: 300 },    // Arcade
      { x: 2650, y: 380, viewAngle: 90, viewDistance: 320 },    // Play Place
      { x: 3200, y: 380, viewAngle: 80, viewDistance: 300 },    // Party A
      { x: 4450, y: 380, viewAngle: 80, viewDistance: 280 },    // Party C

      // FLOOR 2 cameras (near ceiling y≈780)
      { x: 150, y: 780, viewAngle: 70, viewDistance: 350 },     // Kitchen
      { x: 800, y: 780, viewAngle: 80, viewDistance: 350 },     // Backstage
      { x: 1650, y: 780, viewAngle: 90, viewDistance: 320 },    // Break Room
      { x: 2800, y: 780, viewAngle: 90, viewDistance: 370 },    // Security
      { x: 3600, y: 780, viewAngle: 70, viewDistance: 350 },    // Manager
      { x: 4400, y: 780, viewAngle: 80, viewDistance: 350 },    // Storage

      // FLOOR 3 cameras (near ceiling y≈1180)
      { x: 400, y: 1180, viewAngle: 100, viewDistance: 450 },   // Animatronics Workshop
      { x: 1600, y: 1180, viewAngle: 80, viewDistance: 350 },   // Maintenance
      { x: 3000, y: 1180, viewAngle: 90, viewDistance: 400 },   // Server Room
      { x: 4200, y: 1180, viewAngle: 80, viewDistance: 350 },   // Observation

      // Outside cam
      { x: -200, y: 250, viewAngle: 100, viewDistance: 450 },
    ];

    const cameras: SecurityCamera[] = [];

    camerasData.forEach((data, index) => {
      const camera = new SecurityCamera({
        id: `camera-${index}`,
        position: { x: data.x, y: data.y },
        size: { width: 20, height: 20 },
        viewAngle: data.viewAngle,
        viewDistance: data.viewDistance,
        rotationSpeed: 30,
        panRange: { min: -45, max: 45 },
      });

      // Camera mesh removed per user request - no visual markers

      cameras.push(camera);
    });

    console.log(`Created ${cameras.length} security cameras`);
    return cameras;
  }

  /**
   * Create interactive object entities - MANY puzzles and switches
   */
  private createInteractiveObjects(): InteractiveObject[] {
    const objectsData = [
      // FLOOR 1 - Interactive objects
      { x: 30, y: 50, type: 'switch' as const },       // Lobby light switch
      { x: 475, y: 135, type: 'lever' as const },       // Service counter bell
      { x: 850, y: 90, type: 'switch' as const },    // Stage light control
      { x: 1590, y: 50, type: 'button' as const },     // Arcade game button 1
      { x: 1935, y: 50, type: 'button' as const },     // Arcade game button 2
      { x: 2441, y: 50, type: 'lever' as const },      // Play place slide lever

      // FLOOR 2 - Employee area controls (y=425: floor surface y=415 + half-height 10)
      { x: -30, y: 475, type: 'switch' as const },     // Kitchen light switch (spaced from door at x=150)
      { x: 960, y: 445, type: 'lever' as const },      // Backstage controls (moved from x=800 to avoid checkpoint overlap)
      { x: 1500, y: 425, type: 'switch' as const },    // Break room light
      { x: 3105, y: 435, type: 'switch' as const },    // Security camera toggle (spaced from security door at x=2600)
      { x: 3650, y: 435, type: 'button' as const },    // Manager intercom (moved from x=3600 to avoid checkpoint overlap)

      // FLOOR 3 - Restricted area controls
      { x: 100, y: 850, type: 'lever' as const },      // Workshop power lever
      { x: 2150, y: 850, type: 'switch' as const },    // Fuse box switch
      { x: 4200, y: 850, type: 'button' as const },    // Observation controls
    ];

    const objects: InteractiveObject[] = [];

    objectsData.forEach((data, index) => {
      const interactiveObject = new InteractiveObject({
        id: `object-${index}`,
        type: data.type,
        position: { x: data.x, y: data.y },
        size: { width: 20, height: 20 },
        requiresPower: false,
        oneTimeUse: false,
        cooldownTime: 2,
        metadata: {},
      });

      // Add the interactive object's mesh to the scene
      const mesh = interactiveObject.getMesh();
      if (mesh) {
        this.scene.add(mesh);
      }

      objects.push(interactiveObject);
    });

    console.log(`Created ${objects.length} interactive objects`);
    return objects;
  }

  /**
   * Create spawn point entities - checkpoints for player respawn
   */
  private createSpawnPoints(): SpawnPoint[] {
    const spawnPointsData = [
      // Player spawn - OUTSIDE in the parking lot
      { x: -400, y: 50, type: 'player' as const, isDefault: true, checkpointName: 'Parking Lot' },

      // FLOOR 1 CHECKPOINTS
      { x: 80, y: 100, type: 'checkpoint' as const, isDefault: false, checkpointName: 'Lobby' },
      { x: 550, y: 140, type: 'checkpoint' as const, isDefault: false, checkpointName: 'Dining Area' },
      { x: 1100, y: 155, type: 'checkpoint' as const, isDefault: false, checkpointName: 'Show Stage' },
      { x: 1831, y: 100, type: 'checkpoint' as const, isDefault: false, checkpointName: 'Arcade' },
      { x: 2750, y: 200, type: 'checkpoint' as const, isDefault: false, checkpointName: 'Play Place' },
      { x: 3200, y: 150, type: 'checkpoint' as const, isDefault: false, checkpointName: 'Party Room A' },
      { x: 3800, y: 150, type: 'checkpoint' as const, isDefault: false, checkpointName: 'Party Room B' },
      { x: 4450, y: 150, type: 'checkpoint' as const, isDefault: false, checkpointName: 'Party Room C' },

      // FLOOR 2 CHECKPOINTS
      { x: 100, y: 490, type: 'checkpoint' as const, isDefault: false, checkpointName: 'Kitchen' },
      { x: 900, y: 600, type: 'checkpoint' as const, isDefault: false, checkpointName: 'Backstage' },
      { x: 1650, y: 450, type: 'checkpoint' as const, isDefault: false, checkpointName: 'Break Room' },
      { x: 2920, y: 450, type: 'checkpoint' as const, isDefault: false, checkpointName: 'Security Office' },
      { x: 3500, y: 520, type: 'checkpoint' as const, isDefault: false, checkpointName: 'Manager\'s Office' },
      { x: 4425, y: 450, type: 'checkpoint' as const, isDefault: false, checkpointName: 'Storage' },

      // FLOOR 3 CHECKPOINTS
      { x: 400, y: 850, type: 'checkpoint' as const, isDefault: false, checkpointName: 'Animatronics Workshop' },
      { x: 1600, y: 850, type: 'checkpoint' as const, isDefault: false, checkpointName: 'Maintenance' },
      { x: 3100, y: 850, type: 'checkpoint' as const, isDefault: false, checkpointName: 'Server Room' },
      { x: 4200, y: 850, type: 'checkpoint' as const, isDefault: false, checkpointName: 'Observation Deck' },

      // Enemy spawn points
      { x: 1100, y: 330, type: 'enemy' as const, isDefault: false, checkpointName: 'Stage Robot' },
      { x: 400, y: 880, type: 'enemy' as const, isDefault: false, checkpointName: 'Workshop Robot' },
    ];

    const spawnPoints: SpawnPoint[] = [];

    spawnPointsData.forEach((data, index) => {
      const spawnPoint = new SpawnPoint({
        id: `spawn-${index}`,
        position: { x: data.x, y: data.y },
        type: data.type,
        metadata: {
          facingDirection: 'right',
          isDefault: data.isDefault,
          checkpointName: data.checkpointName,
          respawnDelay: 0,
        },
      });

      // Register with checkpoint system
      this.checkpointSystem.registerSpawnPoint(spawnPoint);

      // Add spawn point's mesh to scene for visualization
      const mesh = spawnPoint.getMesh();
      if (mesh) {
        this.scene.add(mesh);
      }

      spawnPoints.push(spawnPoint);
    });

    console.log(`Created ${spawnPoints.length} spawn points (${spawnPointsData.filter(s => s.type === 'checkpoint').length} checkpoints)`);
    return spawnPoints;
  }

  /**
   * Setup interaction manager with all interactable entities
   */
  private setupInteractionManager(): void {
    // Set the player
    this.interactionManager.setPlayer(this.player);

    // Register all doors
    this.doors.forEach(door => {
      this.interactionManager.registerInteractable(door);
    });

    // Register all vents
    this.vents.forEach(vent => {
      this.interactionManager.registerInteractable(vent);
    });

    // Register all elevators
    this.elevators.forEach(elevator => {
      this.interactionManager.registerInteractable(elevator);
    });

    // Register all hiding spots
    this.hidingSpots.forEach(hidingSpot => {
      this.interactionManager.registerInteractable(hidingSpot);
    });

    // Register all interactive objects
    this.interactiveObjects.forEach(obj => {
      this.interactionManager.registerInteractable(obj);
    });

    // Register all checkpoints (only checkpoint type spawn points)
    const checkpoints = this.spawnPoints.filter(sp => sp.getSpawnType() === 'checkpoint');
    console.log(`🎯 Registering ${checkpoints.length} checkpoints with InteractionManager`);
    checkpoints.forEach(checkpoint => {
      console.log(`  - Registering checkpoint: ${checkpoint.getCheckpointName()} at (${checkpoint.position.x}, ${checkpoint.position.y})`);
      this.interactionManager.registerInteractable(checkpoint);
    });

    // Setup interaction callbacks with visual feedback
    this.interactionManager.setOnPromptChange((prompt) => {
      // Update the interaction prompt UI
      const promptElement = document.getElementById('interaction-prompt');
      if (!promptElement) {
        // Create the prompt element if it doesn't exist
        const newPrompt = document.createElement('div');
        newPrompt.id = 'interaction-prompt';
        newPrompt.style.position = 'fixed';
        newPrompt.style.bottom = '100px'; // Bottom of screen
        newPrompt.style.left = '50%';
        newPrompt.style.transform = 'translateX(-50%)';
        newPrompt.style.background = 'rgba(0, 0, 0, 0.9)';
        newPrompt.style.color = '#00ff00';
        newPrompt.style.padding = '12px 25px';
        newPrompt.style.borderRadius = '8px';
        newPrompt.style.border = '2px solid #00ff00';
        newPrompt.style.fontSize = '16px';
        newPrompt.style.fontFamily = 'monospace';
        newPrompt.style.fontWeight = 'bold';
        newPrompt.style.zIndex = '9000';
        newPrompt.style.pointerEvents = 'none';
        newPrompt.style.textAlign = 'center';
        document.body.appendChild(newPrompt);
      }

      const promptEl = document.getElementById('interaction-prompt');
      if (promptEl) {
        if (prompt) {
          // For doors, show "Enter <Room>" or "Exit <Room>" depending on which side the player is on
          let displayMsg = prompt.message;
          if (prompt.target.type === 'door' && prompt.message === 'Open Door') {
            const label = (prompt.target as any).doorLabel as string | undefined;
            const backLabel = (prompt.target as any).doorBackLabel as string | undefined;
            const doorId = (prompt.target as any).doorId as string | undefined;
            if (label) {
              const isInside = doorId ? this.doorReturnPositions.has(doorId) : false;
              // Inside room: show what's on the other side (where you came from)
              // Outside room: show what you're entering
              displayMsg = isInside ? `Enter ${backLabel ?? label}` : `Enter ${label}`;
            }
          }
          promptEl.innerHTML = `Press [E] ${displayMsg}`;
          promptEl.style.display = 'block';
          // Position at BOTTOM of screen near player
          promptEl.style.top = 'auto';
          promptEl.style.bottom = '100px';
          console.log(`🎮 Interaction available: ${prompt.message}`);
        } else {
          // Before hiding, check if player is near a container, monitor, or call button
          // so those also show the green bar (required by the interaction gate).
          let specialMsg: string | null = null;

          for (const container of this.hiddenContainers) {
            const dx = Math.abs(this.player.position.x - container.x);
            const dy = Math.abs(this.player.position.y - container.y);
            if (dx < 60 && dy < 60) {
              specialMsg = container.searched ? 'Already Searched' : `Search`;
              break;
            }
          }

          if (!specialMsg) {
            const monitorX = 2720, monitorY = 570;
            const mdx = Math.abs(this.player.position.x - monitorX);
            const mdy = Math.abs(this.player.position.y - monitorY);
            if (mdx < 160 && mdy < 180) {
              specialMsg = this.cameraUIOpen ? 'Close Cameras' : 'Open Cameras';
            }
          }

          if (!specialMsg) {
            for (const btn of this.callButtons) {
              const dx = Math.abs(this.player.position.x - btn.x);
              const dy = Math.abs(this.player.position.y - btn.y);
              if (dx < 80 && dy < 80) {
                specialMsg = `Call Elevator to Floor ${btn.floorIndex + 1}`;
                break;
              }
            }
          }

          if (specialMsg) {
            promptEl.innerHTML = `Press [E] ${specialMsg}`;
            promptEl.style.display = 'block';
            promptEl.style.top = 'auto';
            promptEl.style.bottom = '100px';
          } else {
            promptEl.style.display = 'none';
          }
        }
      }
    });

    this.interactionManager.setOnInteractionComplete((target) => {
      console.log(`✅ Interacted with: ${target.id} (${target.type})`);

      // Handle door interactions - SHOW CLUE MESSAGE and TELEPORT TO ROOM
      if (target.type === 'door') {
        const door = target as Door;
        const clue = (target as any).clueMessage;
        const label = (target as any).doorLabel;
        const roomX = (target as any).roomTeleportX;
        const roomY = (target as any).roomTeleportY;

        // Teleport is handled by the direct E key handler below (onInteractionComplete
        // fires too late in the frame - after inputManager.update() clears isKeyPressed)

        if (clue) {
          // Create clue notification popup in TOP-LEFT area (between health bar and clock)
          const cluePopup = document.createElement('div');
          cluePopup.id = 'clue-popup'; // Add ID for E key close
          cluePopup.style.position = 'fixed';
          cluePopup.style.top = '20px'; // Align with health bar
          cluePopup.style.left = '240px'; // After health bar area (~220px)
          cluePopup.style.background = 'rgba(0, 0, 0, 0.95)';
          cluePopup.style.color = '#ffff00';
          cluePopup.style.padding = '12px 18px';
          cluePopup.style.borderRadius = '8px';
          cluePopup.style.border = '2px solid #00ff00';
          cluePopup.style.fontSize = '13px';
          cluePopup.style.fontFamily = 'monospace';
          cluePopup.style.fontWeight = 'bold';
          cluePopup.style.zIndex = '10000';
          cluePopup.style.maxWidth = '320px';
          cluePopup.style.textAlign = 'left';
          cluePopup.style.boxShadow = '0 0 15px rgba(0, 255, 0, 0.4)';
          cluePopup.innerHTML = `
            <div style="color: #ff69b4; margin-bottom: 6px; font-size: 14px;">📍 ${label}</div>
            <div style="color: #ffff00;">"${clue}"</div>
            <div style="color: #888; margin-top: 8px; font-size: 11px;">Press [E] or click to close</div>
          `;
          document.body.appendChild(cluePopup);

          // Remove popup after 10 seconds, or on click, or on E key press
          const removePopup = () => {
            if (document.body.contains(cluePopup)) {
              document.body.removeChild(cluePopup);
            }
            document.removeEventListener('click', removePopup);
            document.removeEventListener('keydown', eKeyClose);
          };

          // E key close handler
          const eKeyClose = (e: KeyboardEvent) => {
            if (e.code === 'KeyE') {
              removePopup();
            }
          };

          setTimeout(removePopup, 10000); // Auto-close after 10 seconds
          setTimeout(() => {
            document.addEventListener('click', removePopup);
            document.addEventListener('keydown', eKeyClose);
          }, 100);

          console.log(`🔍 CLUE REVEALED: ${clue}`);
        }
      }

      // Handle hiding spot interactions
      if (target.type === 'hiding_spot') {
        const hidingSpot = target as HidingSpot;

        // Check if player is already hiding
        if (hidingSpot.getIsPlayerHiding()) {
          // Exit the hiding spot
          hidingSpot.exit();
          this.player.unhide();
          store.dispatch(setHiding(false));
          console.log(`👤 Exited hiding spot: ${hidingSpot.id}`);
        } else {
          // Try to hide in the spot
          const success = hidingSpot.hide(this.player);
          if (success) {
            this.player.hide();
            store.dispatch(setHiding(true));
            console.log(`🫣 Hiding in: ${hidingSpot.id} (${hidingSpot.getSpotType()})`);
            console.log(`⏱️  Time until detected: ${hidingSpot.getTimeRemaining()}s`);
          } else {
            console.log(`❌ Cannot hide in: ${hidingSpot.id} (spot full or too far)`);
          }
        }
      }

      // Handle interactive object interactions
      if (target.type === 'interactive_object') {
        const obj = target as InteractiveObject;
        console.log(`  Object state: ${obj.getIsActivated() ? 'ACTIVATED' : 'DEACTIVATED'}`);
      }
    });

    console.log('InteractionManager: Setup complete with all interactables');
  }

  /**
   * Handle input
   */
  private handleInput(deltaTime: number): void {
    // Update InputManager state (must be called before entities read input)
    this.inputManager.update();

    // While camera UI is open, skip all player movement input
    if (this.cameraUIOpen) return;

    // Check if player is inside any moving elevator — lock horizontal movement
    this.playerInMovingElevator = false;
    for (const elev of this.elevators) {
      const dir = elev.getDirection();
      if (dir === 'up' || dir === 'down') {
        const dx = Math.abs(this.player.position.x - elev.position.x);
        const dy = Math.abs(this.player.position.y - elev.position.y);
        if (dx < elev.size.width / 2 + 20 && dy < elev.size.height * 1.5) {
          this.playerInMovingElevator = true;
          this.player.velocity.x = 0;
          // ======== COPY FROM HERE ========
          // Freeze robots and wipe their knowledge of the player when elevator moves
          this.robots.forEach(r => {
            if (r.position.x > 2180 && r.position.x < 2420) {
              r.position.x = r.position.x < 2300 ? 2180 : 2420;
              r.velocity.x = 0;
            }
            r.lastKnownPlayerPosition = null;
            this.robotSuspicionMeters.set(r.id, 0);
            this.robotsInChase.delete(r.id);
          });
          // ======== COPY TO HERE ========
          break;
        }
      }
    }

    if (this.playerInMovingElevator) return;

    // Debug: Check if Space key is detected at InputManager level
    if (debugSettings.enableInputLogging && (this.inputManager.isKeyPressed('Space') || this.inputManager.isKeyDown('Space'))) {
      console.log('🎮 [TestScene] Space key detected in handleInput! Pressed:', this.inputManager.isKeyPressed('Space'), 'Down:', this.inputManager.isKeyDown('Space'));
    }

    // Process player input IMMEDIATELY after InputManager update
    // This ensures pressed keys are detected before they're cleared next frame
    this.player.update(deltaTime);



    // Reset player position if it falls too far
    if (this.player.position.y < -500) {
      this.player.position.x = -400;
      this.player.position.y = 100;
      this.player.velocity.x = 0;
      this.player.velocity.y = 0;
    }
  }

  /**
   * Initialize scene
   */
  public async initialize(): Promise<void> {
    console.log('TestScene: Initializing...');

    // Clear any existing notifications from previous sessions
    store.dispatch(clearNotifications());

    // Initialize InputManager with default key bindings
    const { DEFAULT_KEY_BINDINGS } = await import('../../state/types');
    this.inputManager.setKeyBindings(DEFAULT_KEY_BINDINGS);
    console.log('TestScene: InputManager initialized with key bindings');

    // DIRECT KEYBOARD HANDLER - Handles jump and movement directly AND triggers sound effects
    const jumpHandler = (e: KeyboardEvent) => {
      // Block all movement input while riding a moving elevator
      if (this.playerInMovingElevator) return;

      if (debugSettings.noclip) {
        // Noclip vertical movement: W/ArrowUp = fly up, S/ArrowDown = fly down
        const NOCLIP_SPEED = 450;
        if (e.code === 'KeyW' || e.code === 'ArrowUp') {
          this.player.velocity.y = NOCLIP_SPEED;
        }
        if (e.code === 'KeyS' || e.code === 'ArrowDown') {
          this.player.velocity.y = -NOCLIP_SPEED;
        }
        // Space still does nothing in noclip (prevent jump logic below)
      } else if (e.code === 'Space' || e.code === 'KeyW' || e.code === 'ArrowUp') {
        if (debugSettings.enableInputLogging) {
          console.log('🚀 [TestScene] Jump key detected: ' + e.code);
        }
        // Use physics engine to jump - INCREASED JUMP HEIGHT
        const success = this.physicsEngine.jump(this.player.id, this.player.jumpForce);
        if (success) {
          // Manually trigger jump sound since we're bypassing Player.jump()
          playJumpSound();
        }
      }

      // Handle movement keys directly
      if (e.code === 'KeyA' || e.code === 'ArrowLeft') {
        this.physicsEngine.moveHorizontal(this.player.id, -1, 300);
      }
      if (e.code === 'KeyD' || e.code === 'ArrowRight') {
        this.physicsEngine.moveHorizontal(this.player.id, 1, 300);
      }
    };

    const keyUpHandler = (e: KeyboardEvent) => {
      if (e.code === 'KeyA' || e.code === 'ArrowLeft' || e.code === 'KeyD' || e.code === 'ArrowRight') {
        this.physicsEngine.moveHorizontal(this.player.id, 0, 0);
      }
      // Stop vertical noclip movement on key release
      if (debugSettings.noclip && (e.code === 'KeyW' || e.code === 'ArrowUp' || e.code === 'KeyS' || e.code === 'ArrowDown')) {
        this.player.velocity.y = 0;
      }
    };

    // DIRECT E KEY HANDLER for interactions
    const interactHandler = (e: KeyboardEvent) => {
      if (e.code === 'KeyE') {
        console.log('🎮 E KEY PRESSED! Attempting interaction...');

        // Gate: ALL interactions require a visible prompt.
        // call-button-hint is the hint for elevator buttons; interaction-prompt covers everything else.
        {
          const promptEl = document.getElementById('interaction-prompt');
          const hintEl  = document.getElementById('call-button-hint');
          const greenPromptVisible =
            (promptEl !== null && promptEl.style.display !== 'none') ||
            (hintEl   !== null && hintEl.style.display   !== 'none');
          if (!greenPromptVisible) {
            console.log('❌ No green prompt visible — interact blocked');
            return;
          }
        }

        // Check hidden container proximity (searchable lockers, desks, boxes, etc.)
        for (const container of this.hiddenContainers) {
          const dx = Math.abs(this.player.position.x - container.x);
          const dy = Math.abs(this.player.position.y - container.y);
          if (dx < 60 && dy < 60) {
            if (container.searched) {
              store.dispatch(addNotification({ message: 'Already searched — nothing left.', type: 'info', duration: 1500 }));
            } else if (!container.itemId) {
              store.dispatch(addNotification({ message: 'Nothing here.', type: 'info', duration: 1500 }));
              container.searched = true;
              (container.mesh.material as THREE.MeshBasicMaterial).color.setHex(0x2a2a2a);
            } else {
              // Grant the item
              const itemId = container.itemId;
              const itemName = container.itemName;
              let granted = false;

              if (itemId === 'battery_pack') {
                this.player.refillFlashlight(30);
                granted = inventorySystem.addItem('battery_pack', 1);
                store.dispatch(addNotification({ message: `Found: ${itemName} (+30% flashlight)`, type: 'success', duration: 3000 }));
              } else if (itemId === 'health_kit') {
                this.player.heal(50);
                granted = inventorySystem.addItem('health_kit', 1);
                store.dispatch(addNotification({ message: `Found: ${itemName} (+50 HP)`, type: 'success', duration: 3000 }));
              } else {
                granted = inventorySystem.addItem(itemId, 1);
                const isKey = ['employee_key', 'manager_key', 'restricted_key'].includes(itemId);
                store.dispatch(addNotification({ message: `Found: ${itemName}`, type: isKey ? 'success' : 'info', duration: 4000 }));
              }

              if (granted || itemId === 'battery_pack' || itemId === 'health_kit') {
                container.searched = true;
                container.itemId = null;
                (container.mesh.material as THREE.MeshBasicMaterial).color.setHex(0x2a2a2a);
                console.log(`📦 Searched ${container.id}: found ${itemName}`);
              }
            }
            return; // Don't process other interactions
          }
        }

        // Check security monitor bank proximity (FNAF camera system)
        {
          const monitorX = 2720, monitorY = 570;
          const mdx = Math.abs(this.player.position.x - monitorX);
          const mdy = Math.abs(this.player.position.y - monitorY);
          if (mdx < 160 && mdy < 180) {
            if (this.cameraUIOpen) {
              this.closeCameraUI();
            } else {
              this.openCameraUI();
            }
            return;
          }
        }

        // Check call button proximity (not registered with InteractionManager)
        for (const btn of this.callButtons) {
          const dx = Math.abs(this.player.position.x - btn.x);
          const dy = Math.abs(this.player.position.y - btn.y);
          if (dx < 80 && dy < 80) {
            const elevator = this.elevators[0];
            if (elevator) {
              if (!elevator.isIdle()) {
                store.dispatch(addNotification({ message: 'Elevator is already moving...', type: 'info', duration: 1500 }));
              } else if (elevator.isIdleAtFloor(btn.floorIndex)) {
                store.dispatch(addNotification({ message: `Elevator is already on this floor`, type: 'info', duration: 1500 }));
              } else {
                elevator.callToFloor(btn.floorIndex);
                store.dispatch(addNotification({ message: `Calling elevator to Floor ${btn.floorIndex + 1}...`, type: 'info', duration: 2500 }));
              }
            }
            return; // Don't process other interactions
          }
        }

        // Direct door check — search this.doors by proximity each keypress so we never rely
        // on nearbyInteractables being up-to-date (it's from the previous frame).
        {
          let closestDoor: typeof this.doors[0] | null = null;
          let closestDist = Infinity;
          for (const d of this.doors) {
            const dx = this.player.position.x - d.position.x;
            const dy = this.player.position.y - d.position.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < 100 && dist < closestDist) {
              closestDoor = d;
              closestDist = dist;
            }
          }
          if (closestDoor) {
            const door = closestDoor as any;
            const doorLabel = door.doorLabel;
            const roomX = door.roomTeleportX;
            const roomY = door.roomTeleportY;
            let didOpen = false;

            if (door.getIsOpen && door.getIsOpen()) {
              door.close();
              console.log('🚪 Door closed');
            } else if (door.tryOpen) {
              const success = door.tryOpen(this.player);
              didOpen = success;
              console.log(`🚪 Door open attempt: ${success ? 'SUCCESS' : 'FAILED (locked?)'}`);
            }

            if (didOpen && roomX !== undefined && roomY !== undefined) {
              const platformOffset = 30;
              const doorId = door.doorId;
              const returnPos = this.doorReturnPositions.get(doorId);

              // Toggle: first open → enter room; second open → exit near the door
              if (returnPos) {
                // Front Entrance is one-way during the night — player cannot leave
                if (doorLabel === 'Front Entrance') {
                  store.dispatch(addNotification({ message: "The doors are locked. Survive the night!", type: 'info', duration: 3000 }));
                  // Re-close the door without teleporting
                  setTimeout(() => { if (door.getIsOpen && door.getIsOpen()) door.close(); }, 600);
                  this.player.velocity.x = 0;
                  return;
                }
                // Exiting — place player just outside the door frame on the opposite side from where they are
                const exitOffsetX = this.player.position.x > door.position.x ? -80 : 80;
                this.player.position.x = door.position.x + exitOffsetX;
                this.player.position.y = door.position.y + platformOffset;
                this.doorReturnPositions.delete(doorId);
                console.log(`🚪 Exited ${doorLabel}! → just outside door at (${this.player.position.x}, ${this.player.position.y})`);
              } else {
                // Entering — save current position then place player just inside the door frame
                this.doorReturnPositions.set(doorId, {
                  x: this.player.position.x,
                  y: this.player.position.y,
                });
                // Place player 80px past the door on the room side
                const entryOffsetX = roomX > door.position.x ? 80 : -80;
                this.player.position.x = door.position.x + entryOffsetX;
                this.player.position.y = door.position.y + platformOffset;
                console.log(`🚪 Entered ${doorLabel}! → just inside door at (${this.player.position.x}, ${this.player.position.y})`);
              }

              this.player.velocity.x = 0;
              this.player.velocity.y = 0;

              setTimeout(() => {
                if (door.getIsOpen && door.getIsOpen()) {
                  door.close();
                }
              }, 600);
              return; // Teleported — don't process other interactions
            }
          }
        }

        // Direct vent check — works even when the green bar just disappeared
        for (const vent of this.vents) {
          const exitPos = (vent as any).getExitPosition();
          const entrancePos = (vent as any).getEntrancePosition();
          // Check entrance
          const dxEn = Math.abs(this.player.position.x - entrancePos.x);
          const dyEn = Math.abs(this.player.position.y - entrancePos.y);
          if (dxEn < 80 && dyEn < 80) {
            this.player.position.x = exitPos.x;
            this.player.position.y = exitPos.y;
            this.player.velocity.x = 0;
            this.player.velocity.y = 0;
            console.log(`✨ Teleported through vent to (${exitPos.x}, ${exitPos.y})`);
            return;
          }
          // Check exit — teleport back to entrance
          const dxEx = Math.abs(this.player.position.x - exitPos.x);
          const dyEx = Math.abs(this.player.position.y - exitPos.y);
          if (dxEx < 80 && dyEx < 80) {
            this.player.position.x = entrancePos.x;
            this.player.position.y = entrancePos.y;
            this.player.velocity.x = 0;
            this.player.velocity.y = 0;
            console.log(`✨ Teleported BACK through vent to entrance (${entrancePos.x}, ${entrancePos.y})`);
            return;
          }
        }

        // Direct hiding spot check — works even when the green bar just disappeared
        for (const spot of this.hidingSpots) {
          const dx = Math.abs(this.player.position.x - spot.position.x);
          const dy = Math.abs(this.player.position.y - spot.position.y);
          if (dx < 80 && dy < 80) {
            const anySpot = spot as any;
            if (anySpot.getIsPlayerHiding?.()) {
              anySpot.exit();
              this.player.unhide();
            } else if (!anySpot.isFull?.()) {
              const success = anySpot.hide(this.player);
              if (success) this.player.hide();
            }
            return;
          }
        }

        // Fallback: InteractionManager nearbyInteractables (elevators, interactive objects, checkpoints)
        const nearbyInteractables = (this.interactionManager as any).nearbyInteractables;
        console.log(`📍 Nearby interactables: ${nearbyInteractables?.length || 0}`);

        if (nearbyInteractables && nearbyInteractables.length > 0) {
          const target = nearbyInteractables[0];
          console.log(`🎯 Interacting with: ${target.id} (${target.type})`);

          if (target.type === 'elevator') {
            const elevator = target as any;
            const pos = elevator.getInsidePosition();
            this.player.position.x = pos.x;
            this.player.position.y = pos.y;
            this.player.velocity.x = 0;
            this.player.velocity.y = 0;
            console.log(`🛗 Player teleported to elevator at (${pos.x}, ${pos.y})`);
            store.dispatch(addNotification({ message: 'U/D to change floor | walk out to exit', type: 'info', duration: 3000 }));
          } else if (target.type === 'interactive_object') {
            const obj = target as any;
            if (obj.interact) {
              obj.interact(this.player);
              console.log(`⚙️ Activated ${obj.getObjectType()}`);
            }
          }

          // Trigger the interaction complete callback manually
          const callback = (this.interactionManager as any).onInteractionComplete;
          if (callback) {
            callback(target);
          }
        } else {
          console.log('❌ No nearby interactables to interact with');
        }
      }
    };

    // DIRECT U/D KEY HANDLER for elevator controls
    const elevatorHandler = (e: KeyboardEvent) => {
      if (e.code !== 'KeyU' && e.code !== 'KeyD') return;
      if (e.repeat) return;

      this.elevators.forEach(elevator => {
        const dx = Math.abs(this.player.position.x - elevator.position.x);
        const dy = Math.abs(this.player.position.y - elevator.position.y);
        const nearElevator = dx < elevator.size.width * 1.5 && dy < elevator.size.height * 1.5;

        if (!nearElevator || !elevator.isIdle()) return;

        if (e.code === 'KeyU') {
          const result = elevator.goUp();
          if (result === 'at_top') {
            store.dispatch(addNotification({ message: 'Highest floor reached', type: 'info', duration: 2000 }));
          } else if (result === 'moved') {
            console.log(`🛗 Elevator going UP to floor ${elevator.getCurrentFloorIndex() + 1}`);
          }
        } else if (e.code === 'KeyD') {
          const result = elevator.goDown();
          if (result === 'at_bottom') {
            store.dispatch(addNotification({ message: 'Lowest floor reached', type: 'info', duration: 2000 }));
          } else if (result === 'moved') {
            console.log(`🛗 Elevator going DOWN to floor ${elevator.getCurrentFloorIndex() + 1}`);
          }
        }
      });
    };

    window.addEventListener('keydown', jumpHandler);
    window.addEventListener('keydown', interactHandler);
    window.addEventListener('keydown', elevatorHandler);
    window.addEventListener('keyup', keyUpHandler);

    // Expose noclip toggle for the debug panel
    (window as any).toggleNoclip = () => {
      debugSettings.toggleNoclip();
      if (debugSettings.noclip) {
        this.physicsEngine.setEntityGravity(this.player.id, 0);
        this.player.velocity.y = 0;
      } else {
        this.physicsEngine.setEntityGravity(this.player.id, 1.0);
      }
      return debugSettings.noclip;
    };

    // Setup game loop callbacks
    this.gameLoop.setInputCallback((dt) => this.handleInput(dt));
    this.gameLoop.setUpdateCallback((dt) => this.update(dt));
    this.gameLoop.setRenderCallback(() => this.render());

    // Start game loop
    this.gameLoop.start();

    // AUTO-FOCUS the window so keyboard controls work immediately
    window.focus();

    console.log('✅ TestScene: Initialized and running!');
    console.log('🎮 Controls: A/D or Arrow Keys to move, Space/W/ArrowUp to jump');
    console.log('📍 Look for: GREEN player box, RED enemy boxes, BROWN platforms');
    console.log('🔊 Sound: Jump and landing sounds should play');

    // CREATE UI ELEMENTS
    this.createMinimap();
    this.createCollectiblesUI();
    this.createObjectivesUI();

    // Alert user that game is ready (helps with debugging)
    console.log('');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('   🎮 GAME READY - START PLAYING! 🎮');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('');
  }

  /**
   * Create background elements - night sky, stars, moon, city silhouette
   * All placed at z=-10 so they render behind everything
   */
  private createBackground(): void {
    // All background elements are stored so we can move them with the camera
    // This makes them appear STATIC (not scrolling) - like a real background

    // Night sky backdrop - massive plane
    const skyGeo = new THREE.PlaneGeometry(3000, 2000);
    const skyMat = new THREE.MeshBasicMaterial({ color: 0x050515 });
    const sky = new THREE.Mesh(skyGeo, skyMat);
    sky.position.set(0, 200, -20); // Relative offset from camera
    this.scene.add(sky);
    this.backgroundMeshes.push(sky);

    // Stars - scattered across sky (positions are offsets from camera center)
    const starPositions = [
      { x: -600, y: 400, s: 3, c: 0xFFFFFF }, { x: -400, y: 550, s: 2, c: 0xCCCCFF },
      { x: -200, y: 350, s: 4, c: 0xFFFFFF }, { x: 0, y: 600, s: 2, c: 0xFFFFDD },
      { x: 200, y: 450, s: 3, c: 0xCCCCFF }, { x: 400, y: 650, s: 2, c: 0xFFFFFF },
      { x: 500, y: 380, s: 4, c: 0xFFFFDD }, { x: 700, y: 520, s: 3, c: 0xFFFFFF },
      { x: -700, y: 700, s: 2, c: 0xCCCCFF }, { x: -350, y: 750, s: 3, c: 0xFFFFFF },
      { x: -100, y: 300, s: 4, c: 0xFFFFDD }, { x: 100, y: 800, s: 2, c: 0xCCCCFF },
      { x: 300, y: 250, s: 3, c: 0xFFFFFF }, { x: 600, y: 350, s: 2, c: 0xFFFFDD },
      { x: -500, y: 500, s: 3, c: 0xCCCCFF }, { x: -250, y: 680, s: 2, c: 0xFFFFFF },
      { x: 150, y: 720, s: 4, c: 0xFFFFDD }, { x: 450, y: 280, s: 3, c: 0xFFFFFF },
      { x: -650, y: 330, s: 2, c: 0xCCCCFF }, { x: 650, y: 480, s: 3, c: 0xFFFFDD },
      { x: -450, y: 820, s: 2, c: 0xFFFFFF }, { x: 750, y: 700, s: 4, c: 0xCCCCFF },
      { x: -300, y: 200, s: 2, c: 0xFFFFFF }, { x: 350, y: 580, s: 3, c: 0xFFFFDD },
      { x: -150, y: 900, s: 2, c: 0xCCCCFF }, { x: 550, y: 850, s: 3, c: 0xFFFFFF },
    ];
    starPositions.forEach(s => {
      const geo = new THREE.PlaneGeometry(s.s, s.s);
      const mat = new THREE.MeshBasicMaterial({ color: s.c });
      const star = new THREE.Mesh(geo, mat);
      star.position.set(s.x, s.y, -18);
      this.scene.add(star);
      this.backgroundMeshes.push(star);
    });

    // Moon - large pale circle (upper right of view)
    const moonGeo = new THREE.CircleGeometry(55, 32);
    const moonMat = new THREE.MeshBasicMaterial({ color: 0xEEEECC });
    const moon = new THREE.Mesh(moonGeo, moonMat);
    moon.position.set(500, 650, -17);
    this.scene.add(moon);
    this.backgroundMeshes.push(moon);

    // Moon glow
    const glowGeo = new THREE.CircleGeometry(80, 32);
    const glowMat = new THREE.MeshBasicMaterial({ color: 0x444433, transparent: true, opacity: 0.4 });
    const glow = new THREE.Mesh(glowGeo, glowMat);
    glow.position.set(500, 650, -17.5);
    this.scene.add(glow);
    this.backgroundMeshes.push(glow);

    // City silhouette - dark building shapes spread across the background
    const buildingShapes = [
      { x: -900, w: 140, h: 250, c: 0x0A0A15 }, { x: -720, w: 100, h: 380, c: 0x080812 },
      { x: -580, w: 120, h: 200, c: 0x0C0C18 }, { x: -430, w: 160, h: 420, c: 0x070710 },
      { x: -250, w: 110, h: 300, c: 0x090915 }, { x: -100, w: 130, h: 340, c: 0x0B0B14 },
      { x: 80, w: 100, h: 260, c: 0x080812 },  { x: 230, w: 140, h: 400, c: 0x0A0A15 },
      { x: 400, w: 110, h: 280, c: 0x070710 },  { x: 550, w: 90, h: 320, c: 0x090915 },
      { x: 680, w: 160, h: 360, c: 0x0B0B14 },  { x: 860, w: 120, h: 240, c: 0x0C0C18 },
    ];
    buildingShapes.forEach(b => {
      const geo = new THREE.PlaneGeometry(b.w, b.h);
      const mat = new THREE.MeshBasicMaterial({ color: b.c });
      const bldg = new THREE.Mesh(geo, mat);
      bldg.position.set(b.x, b.h / 2 - 200, -16);
      this.scene.add(bldg);
      this.backgroundMeshes.push(bldg);
    });

    // Underground fill - dark brown earth below ground (this one stays in world space)
    const earthGeo = new THREE.PlaneGeometry(12000, 1000);
    const earthMat = new THREE.MeshBasicMaterial({ color: 0x1A1008 });
    const earth = new THREE.Mesh(earthGeo, earthMat);
    earth.position.set(2300, -530, -5);
    this.scene.add(earth);
    // NOTE: earth is NOT added to backgroundMeshes - it stays in world space

    console.log('Background created: static sky, stars, moon, city silhouette (follows camera)');
  }

  /**
   * Create minimap in lower left corner
   */
  private createMinimap(): void {
    const minimap = document.createElement('div');
    minimap.id = 'minimap';
    minimap.style.position = 'fixed';
    minimap.style.bottom = '20px';
    minimap.style.left = '20px';
    minimap.style.width = '200px';
    minimap.style.height = '200px';
    minimap.style.background = 'rgba(0, 0, 0, 0.8)';
    minimap.style.border = '2px solid #00ff00';
    minimap.style.borderRadius = '5px';
    minimap.style.zIndex = '9000';
    minimap.style.pointerEvents = 'none';

    // Create canvas for minimap
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 200;
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    minimap.appendChild(canvas);

    document.body.appendChild(minimap);

    // Store canvas for updating
    (this as any).minimapCanvas = canvas;
    (this as any).minimapCtx = canvas.getContext('2d');
  }

  // ─────────────────────────────────────────────
  // VISUAL FEEDBACK HELPERS
  // ─────────────────────────────────────────────

  /** Creates the crosshair overlay — grey normally, green when taser is equipped */
  private createCrosshair(): void {
    const el = document.createElement('div');
    el.id = 'game-crosshair';
    el.style.cssText = [
      'position:fixed',
      'width:18px',
      'height:18px',
      'border-radius:50%',
      'border:2px solid rgba(180,180,180,0.7)',
      'pointer-events:none',
      'z-index:9500',
      'box-shadow:0 0 0 1px rgba(0,0,0,0.5)',
      'transition:border-color 0.15s,box-shadow 0.15s',
      'transform:translate(-50%,-50%)',
    ].join(';');

    // Centre dot
    const dot = document.createElement('div');
    dot.style.cssText = [
      'position:absolute',
      'left:50%',
      'top:50%',
      'transform:translate(-50%,-50%)',
      'width:3px',
      'height:3px',
      'border-radius:50%',
      'background:rgba(180,180,180,0.8)',
    ].join(';');
    el.appendChild(dot);

    document.body.appendChild(el);
    this.crosshairEl = el;

    // Mouse-down listener for taser shoot — only fires when an enemy is within aim range
    this.onMouseDownBound = (e: MouseEvent) => {
      if (e.button !== 0) return; // left click only
      if (this.taserCooldown > 0) return;
      if (!inventorySystem.hasItem('pistol')) return;
      // Require an actual target within aim lock range
      const AIM_LOCK_RANGE = 500;
      const hasTarget = this.robots.some(robot => {
        const dx = robot.position.x - this.player.position.x;
        const dy = robot.position.y - this.player.position.y;
        return Math.sqrt(dx * dx + dy * dy) < AIM_LOCK_RANGE;
      });
      if (!hasTarget) return;
      this.taserCooldown = 1.0; // 1-second cooldown between shots
      playTaserPickup();
      this.visualEffects.spawnAlertBurst(this.player.position.x, this.player.position.y);
    };
    window.addEventListener('mousedown', this.onMouseDownBound);
  }

  /** Creates the DOM overlay used to render suspicion meters above robots */
  private createSuspicionOverlay(): void {
    const el = document.createElement('div');
    el.id = 'suspicion-overlay';
    el.style.cssText = 'position:fixed;inset:0;pointer-events:none;z-index:8990;overflow:hidden;';
    document.body.appendChild(el);
    this.suspicionOverlay = el;
  }

  /** Projects a world-space point to CSS screen coordinates using the orthographic camera */
  private worldToScreen(wx: number, wy: number): { x: number; y: number } {
    const v = new THREE.Vector3(wx, wy, 0).project(this.camera);
    return {
      x: (v.x + 1) / 2 * window.innerWidth,
      y: -(v.y - 1) / 2 * window.innerHeight,
    };
  }

  /** Redraws suspicion meters above each robot each frame */
  private updateSuspicionOverlay(): void {
    if (!this.suspicionOverlay) return;
    const html: string[] = [];

    this.robots.forEach(robot => {
      const suspicion = this.robotSuspicionMeters.get(robot.id) ?? 0;
      const state = robot.getAIState();
      if (suspicion <= 0 && state !== 'chase' && state !== 'attack') return;

      const screen = this.worldToScreen(robot.position.x, robot.position.y + robot.size.height / 2 + 22);

      // Bar color: yellow (suspicious) → red (chasing)
      const chasing = state === 'chase' || state === 'attack';
      const fill = chasing ? 100 : suspicion;
      const barColor = chasing ? '#ff2200' : `hsl(${60 - (suspicion / 100) * 60}, 100%, 55%)`;

      html.push(`
        <div style="
          position:absolute;
          left:${screen.x - 22}px;
          top:${screen.y - 6}px;
          width:44px;
          pointer-events:none;
        ">
          <div style="width:100%;height:4px;background:rgba(0,0,0,0.6);border:1px solid #333;border-radius:2px;overflow:hidden;">
            <div style="width:${fill}%;height:100%;background:${barColor};transition:width 0.1s,background 0.2s;"></div>
          </div>
          ${chasing ? `<div style="text-align:center;font-size:9px;color:#ff3300;font-family:monospace;line-height:1;margin-top:1px;text-shadow:0 0 4px #ff0000;">!</div>` : ''}
        </div>
      `);
    });

    this.suspicionOverlay.innerHTML = html.join('');
  }

  /** Brief full-screen red flash on player damage */
  private flashDamageOverlay(): void {
    let el = document.getElementById('damage-flash') as HTMLDivElement | null;
    if (!el) {
      el = document.createElement('div');
      el.id = 'damage-flash';
      el.style.cssText = `
        position:fixed;inset:0;pointer-events:none;z-index:9900;
        background:rgba(200,0,0,0);
        transition:background 0.05s ease-in;
      `;
      document.body.appendChild(el);
    }
    // Flash in then fade out
    el.style.background = 'rgba(200,0,0,0.35)';
    setTimeout(() => {
      if (el) el.style.background = 'rgba(200,0,0,0)';
      el!.style.transition = 'background 0.4s ease-out';
    }, 60);
    setTimeout(() => {
      if (el) el.style.transition = 'background 0.05s ease-in';
    }, 460);
  }

  /**
   * Create collectibles list UI
   */
  private createCollectiblesUI(): void {
    const collectiblesUI = document.createElement('div');
    collectiblesUI.id = 'collectibles-list';
    collectiblesUI.style.position = 'fixed';
    collectiblesUI.style.bottom = '240px';
    collectiblesUI.style.left = '20px';
    collectiblesUI.style.width = '200px';
    collectiblesUI.style.background = 'rgba(0, 0, 0, 0.8)';
    collectiblesUI.style.border = '2px solid #ffff00';
    collectiblesUI.style.borderRadius = '5px';
    collectiblesUI.style.padding = '10px';
    collectiblesUI.style.zIndex = '9000';
    collectiblesUI.style.fontFamily = 'monospace';
    collectiblesUI.style.fontSize = '12px';
    collectiblesUI.style.color = '#ffff00';

    collectiblesUI.innerHTML = `
      <div style="font-weight: bold; margin-bottom: 8px; color: #00ff00;">📦 COLLECTIBLES</div>
      <div id="collectibles-content" style="font-size: 11px;"></div>
    `;

    document.body.appendChild(collectiblesUI);
  }

  /**
   * Create objectives list UI
   */
  private createObjectivesUI(): void {
    const objectivesUI = document.createElement('div');
    objectivesUI.id = 'objectives-list';
    objectivesUI.style.position = 'fixed';
    objectivesUI.style.bottom = '395px';
    objectivesUI.style.left = '20px';
    objectivesUI.style.width = '200px';
    objectivesUI.style.background = 'rgba(0, 0, 0, 0.8)';
    objectivesUI.style.border = '2px solid #ff00ff';
    objectivesUI.style.borderRadius = '5px';
    objectivesUI.style.padding = '10px';
    objectivesUI.style.zIndex = '9000';
    objectivesUI.style.fontFamily = 'monospace';
    objectivesUI.style.fontSize = '12px';
    objectivesUI.style.color = '#ff00ff';

    objectivesUI.innerHTML = `
      <div style="font-weight: bold; margin-bottom: 8px; color: #00ff00;">🎯 OBJECTIVES</div>
      <div id="objectives-content" style="font-size: 11px;">
        <div>• Explore the map</div>
        <div>• Find all clues</div>
        <div>• Collect items</div>
        <div>• Avoid red robots</div>
      </div>
    `;

    document.body.appendChild(objectivesUI);
  }

  /**
   * Update minimap to show player position and map overview
   */
  private updateMinimap(): void {
    const ctx = (this as any).minimapCtx;
    if (!ctx) return;

    const canvas = (this as any).minimapCanvas;
    const mapWidth = 6000; // -1000 to +5000
    const mapHeight = 1600; // -200 to +1400

    // Clear canvas
    ctx.fillStyle = '#0a0a2e';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw platforms
    ctx.fillStyle = '#8B4513';
    this.platforms.forEach(platform => {
      const x = ((platform.position.x + 1000) / mapWidth) * canvas.width;
      const y = ((platform.position.y + 200) / mapHeight) * canvas.height;
      const w = (platform.size.width / mapWidth) * canvas.width;
      const h = (platform.size.height / mapHeight) * canvas.height;
      ctx.fillRect(x, canvas.height - y - h, w, h);
    });

    // Draw enemies
    ctx.fillStyle = '#ff0000';
    this.robots.forEach(robot => {
      const x = ((robot.position.x + 1000) / mapWidth) * canvas.width;
      const y = ((robot.position.y + 200) / mapHeight) * canvas.height;
      ctx.fillRect(x - 2, canvas.height - y - 2, 4, 4);
    });

    // Draw collectibles
    this.collectibles.forEach(collectible => {
      if (!collectible.isItemCollected()) {
        const color = collectible.getGlowColor() || '#ffffff';
        ctx.fillStyle = color;
        const x = ((collectible.position.x + 1000) / mapWidth) * canvas.width;
        const y = ((collectible.position.y + 200) / mapHeight) * canvas.height;
        ctx.fillRect(x - 1, canvas.height - y - 1, 2, 2);
      }
    });

    // Draw player (bright green)
    ctx.fillStyle = '#00ff00';
    const px = ((this.player.position.x + 1000) / mapWidth) * canvas.width;
    const py = ((this.player.position.y + 200) / mapHeight) * canvas.height;
    ctx.fillRect(px - 3, canvas.height - py - 3, 6, 6);

    // Draw border
    ctx.strokeStyle = '#00ff00';
    ctx.lineWidth = 2;
    ctx.strokeRect(0, 0, canvas.width, canvas.height);
  }

  /**
   * Update collectibles list UI
   */
  private updateCollectiblesUI(): void {
    const content = document.getElementById('collectibles-content');
    if (!content) return;

    const counts: Record<string, { c: number; t: number }> = {};
    this.collectibles.forEach(col => {
      const type = col.getCollectibleType();
      if (!counts[type]) counts[type] = { c: 0, t: 0 };
      counts[type]!.t++;
      if (col.isItemCollected()) counts[type]!.c++;
    });

    const icons: Record<string, string> = {
      lore: '📄', battery: '🔋', health: '❤️', upgrade: '⭐',
      weapon: '🔫', taser: '⚡', key: '🗝',
      keycard: '🪪', vhs_tape: '📼', pizza: '🍕', fuse: '🔌', badge: '🏷',
    };
    const rows = Object.entries(counts).map(([type, v]) =>
      `<div style="margin-bottom:4px;">${icons[type] ?? '•'} ${type}: ${v.c}/${v.t}</div>`
    ).join('');
    const totalC = Object.values(counts).reduce((s, v) => s + v.c, 0);
    const totalT = Object.values(counts).reduce((s, v) => s + v.t, 0);

    content.innerHTML = `${rows}
      <div style="margin-top:8px;color:#00ff00;">Total: ${totalC}/${totalT}</div>`;
  }

  /**
   * Update scene
   */
  public update(deltaTime: number): void {
    // BEFORE physics/collision, store current velocities for landing detection
    (this.player as any).previousVelocityY = this.player.velocity.y;
    this.robots.forEach(robot => {
      (robot as any).previousVelocityY = robot.velocity.y;
    });

    // Elevator sync setup - must be BEFORE physicsEngine.update() so prevElevY is pre-physics
    const elevForSync = this.elevators[0];
    const playerPhysics = this.physicsEngine.getPhysics(this.player.id);
    const prevElevY = elevForSync ? elevForSync.position.y : 0;

    // Suppress player gravity when elevator is moving and player is inside it
    if (elevForSync && playerPhysics) {
      const dir = elevForSync.getDirection();
      const dx = Math.abs(this.player.position.x - elevForSync.position.x);
      const dy = Math.abs(this.player.position.y - elevForSync.position.y);
      const insideElev = dx < elevForSync.size.width / 2 + 20 && dy < elevForSync.size.height * 1.5;
      if (dir === 'down' && insideElev) {
        // Fully freeze player vertically so physics does nothing to them this frame
        playerPhysics.gravity = 0;
        playerPhysics.isGrounded = true;
        this.player.velocity.y = 0;
      } else if (dir !== 'down' && !debugSettings.noclip) {
        // Don't restore gravity if noclip is active
        playerPhysics.gravity = 1;
      }
    }

    // Update physics (applies forces and moves entities)
    this.physicsEngine.update(deltaTime);

    // Update collisions (detects collisions and sets grounded state)
    this.collisionSystem.update();

    // Update interaction manager (detect nearby interactables and handle interactions)
    this.interactionManager.update(deltaTime);

    // Update checkpoint system (detect checkpoint activation)
    this.checkpointSystem.update(this.player);

    // Check for player death and respawn
    if (this.player.health <= 0 && !this.player.destroyed) {
      console.log('💀 Player died! Respawning at checkpoint...');
      this.respawnPlayer();
    }

    // Check if player fell below lowest platform and respawn
    if (this.player.position.y < -500) {
      console.log('💀 Player fell off the map! Respawning at checkpoint...');
      this.respawnPlayer();
    }

    // DEBUG: Press K to test respawn
    const inputManager = getInputManager();
    if (inputManager.isKeyPressed('KeyK')) {
      console.log('🔑 K pressed - Testing respawn...');
      console.log(`   Current position: (${this.player.position.x}, ${this.player.position.y})`);
      this.respawnPlayer();
      console.log(`   New position: (${this.player.position.x}, ${this.player.position.y})`);
    }

    // Taser cooldown countdown
    if (this.taserCooldown > 0) this.taserCooldown = Math.max(0, this.taserCooldown - deltaTime);

    // ── Game over: idle detection ───────────────────────────────────────────
    if (!this.isGameOverPending) {
      const movedX = Math.abs(this.player.position.x - this.lastIdlePosition.x);
      const movedY = Math.abs(this.player.position.y - this.lastIdlePosition.y);
      /**
      if (movedX > 10 || movedY > 10) {
        this.idleTimer = 0;
        this.lastIdlePosition = { x: this.player.position.x, y: this.player.position.y };
      } else {
        this.idleTimer += deltaTime;
        if (this.idleTimer >= this.IDLE_TIMEOUT) {
          this.triggerGameOverCutscene("You froze up. The animatronics found you standing still in the dark.");
        }
      }
       */
    }

    // ── Game over: must enter the building ─────────────────────────────────
    if (!this.isGameOverPending) {
      if (this.player.position.x > this.BUILDING_ENTRY_X) {
        this.hasEnteredBuilding = true;
      }
      if (!this.hasEnteredBuilding) {
        this.buildingEntryTimer += deltaTime;
        if (this.buildingEntryTimer >= this.BUILDING_ENTRY_TIMEOUT) {
          this.triggerGameOverCutscene("You never made it inside. Something in the parking lot got you first.", "Wasted your time");
        }
      }
    }

    // Crosshair: lock onto nearest enemy within 500 units, else default offset
    if (this.crosshairEl) {
      // ======== COPY FROM HERE ========
      // Hide crosshair while the pause menu (or any overlay) is open
      this.crosshairEl.style.display = (window as any).gamePaused ? 'none' : 'block';
      if ((window as any).gamePaused) { /* skip position/color update while hidden */ }
      else {
      // ======== COPY TO HERE ========
      const AIM_LOCK_RANGE = 500;
      let nearestRobot: Robot | null = null;
      let nearestDist = AIM_LOCK_RANGE;
      for (const robot of this.robots) {
        const dx = robot.position.x - this.player.position.x;
        const dy = robot.position.y - this.player.position.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < nearestDist) { nearestDist = dist; nearestRobot = robot; }
      }

      let crosshairWorldX: number;
      let crosshairWorldY: number;
      if (nearestRobot) {
        crosshairWorldX = nearestRobot.position.x;
        crosshairWorldY = nearestRobot.position.y;
      } else {
        const facing = this.player.getFacingDirection();
        crosshairWorldX = this.player.position.x + (facing === 'right' ? 200 : -200);
        crosshairWorldY = this.player.position.y;
      }

      const screen = this.worldToScreen(crosshairWorldX, crosshairWorldY);
      this.crosshairEl.style.left = `${screen.x}px`;
      this.crosshairEl.style.top  = `${screen.y}px`;

      const enemyLocked = nearestRobot !== null;
      const color = enemyLocked ? 'rgba(0,255,100,0.9)' : 'rgba(255,60,60,0.9)';
      const glow  = enemyLocked
        ? '0 0 8px rgba(0,255,100,0.8),0 0 0 1px rgba(0,0,0,0.5)'
        : '0 0 8px rgba(255,60,60,0.6),0 0 0 1px rgba(0,0,0,0.5)';
      this.crosshairEl.style.borderColor = color;
      this.crosshairEl.style.boxShadow = glow;
      const dot = this.crosshairEl.firstElementChild as HTMLElement | null;
      if (dot) dot.style.background = color;
      } // end else (not paused)
    }

    // NOW check for landing AFTER collision has been processed
    this.player.checkLanding();

    // Landing dust + shake
    const isNowGrounded = this.physicsEngine.isGrounded(this.player.id);
    const prevVelY = (this.player as any).previousVelocityY ?? 0;
    if (isNowGrounded && !this.playerWasGrounded && prevVelY < -80) {
      this.visualEffects.spawnLandingDust(this.player.position.x, this.player.position.y - this.player.size.height / 2);
    }
    this.playerWasGrounded = isNowGrounded;

    // Update footsteps for player (must be after collision to know if grounded)
    this.player.updateFootsteps();

    // Player is already updated in handleInput() - skip here to avoid double update

    // Ceiling heights per floor (top of each floor's play space, approximate)
    const FLOOR_CEILING_Y = [270, 670, 1060]; // F1, F2, F3 ceiling Y values

    // Update robots with player detection
    this.robots.forEach(robot => {
      // Check landing for ground-based robots
      if (robot.specialAbility !== 'ceiling_crawler') {
        robot.checkLanding();
      }

      // Update footsteps for robots — scale volume by proximity to player
      robot.updateFootsteps();
      const rfDx = robot.position.x - this.player.position.x;
      const rfDy = robot.position.y - this.player.position.y;
      const rfDist = Math.sqrt(rfDx * rfDx + rfDy * rfDy);
      const FOOTSTEP_MAX_RANGE = 700; // px — silent beyond this distance
      const rfT = Math.min(rfDist / FOOTSTEP_MAX_RANGE, 1);
      const rfVolume = 1 - rfT * rfT; // quadratic falloff: full at 0px, silent at 700px
      robot.setFootstepVolume(rfVolume);
      robot.distanceToPlayer = rfDist; // ← distance volume: keep robot's distance current

      const dx = robot.position.x - this.player.position.x;
      const dy = robot.position.y - this.player.position.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // --- Detection logic ---
      const playerVisible = !this.player.isMaskOn() && !this.player.isPlayerHiding();
      const detectCooldown = this.robotDetectCooldowns.get(robot.id) ?? 0;

      // Tick down the post-search cooldown every frame
      if (detectCooldown > 0) {
        this.robotDetectCooldowns.set(robot.id, detectCooldown - deltaTime);
      }

      // Detection: full 360° awareness — robots sense the player in all directions.
      // Crawlers also detect across floors (vent traversal makes them multi-floor threats).
      // Only the stalker is restricted to the same floor so it doesn't alert through ceilings.
      const isCrawler = robot.robotType === 'crawler';
      const sameFloor = isCrawler ? true : Math.abs(dy) < 300;
      const canSee = playerVisible && detectCooldown <= 0 && sameFloor && distance < robot.detectionRange;

      // ── Suspicion meter: robot must "notice" the player over 1.5 s before chasing ──
      const SUSPICION_FILL_RATE = 100 / 1.5;  // fills in 1.5 s
      const SUSPICION_DRAIN_RATE = 100 / 1.0; // drains in 1.0 s when not seen
      let suspicion = this.robotSuspicionMeters.get(robot.id) ?? 0;
      const alreadyChasing = robot.getAIState() === 'chase' || robot.getAIState() === 'attack';

      if (canSee) {
        robot.lastKnownPlayerPosition = { ...this.player.position };
        this.robotLostSightTimers.set(robot.id, 0);

        if (alreadyChasing) {
          // Already chasing — keep suspicion full so it doesn't lapse
          suspicion = 100;
        } else {
          // Build suspicion; trigger chase when full
          suspicion = Math.min(100, suspicion + SUSPICION_FILL_RATE * deltaTime);
          if (suspicion >= 100) {
            robot.transitionToChase();
            if (!this.robotsInChase.has(robot.id)) {
              this.robotsInChase.add(robot.id);
              const detT = Math.min(distance / 700, 1);
              playDetectionFull(1 - detT * detT); // ← distance volume
              this.visualEffects.spawnAlertBurst(robot.position.x, robot.position.y);
              this.visualEffects.triggerScreenShake(4, 0.3);
            }
          }
        }
      } else {
        // Can't see player — drain suspicion
        suspicion = Math.max(0, suspicion - SUSPICION_DRAIN_RATE * deltaTime);

        if (alreadyChasing) {
          // Still chasing but lost sight — count time without sight
          const lostTime = (this.robotLostSightTimers.get(robot.id) ?? 0) + deltaTime;
          this.robotLostSightTimers.set(robot.id, lostTime);

          if (lostTime >= 3.0) {
            // Give up — go search the last known position then resume patrol
            robot.transitionToSearch();
            this.robotsInChase.delete(robot.id);
            this.robotLostSightTimers.set(robot.id, 0);
            suspicion = 0;
          }
        }
      }

      this.robotSuspicionMeters.set(robot.id, suspicion);

      // ── Detection beep: plays while suspicion is rising, pitch and volume scale by suspicion/distance ──
      if (suspicion > 0 && !alreadyChasing) {
        // Beep interval shrinks as suspicion grows: 0.4s at 0% → 0.12s at 100%
        const beepInterval = 0.4 - (suspicion / 100) * 0.28;
        const beepTimer = (this.robotDetectionBeepTimers.get(robot.id) ?? beepInterval) + deltaTime;
        if (beepTimer >= beepInterval) {
          // Distance-based volume: full within 200px, silent at 700px
          const beepDist = Math.sqrt(rfDx * rfDx + rfDy * rfDy);
          const beepT = Math.min(beepDist / 700, 1);
          const beepVolume = (1 - beepT * beepT) * 0.5; // max 0.5, fades with distance
          if (beepVolume > 0.01) {
            // Pitch: 0.8 at 0% suspicion → 2.2 at 100% suspicion
            const pitch = 0.8 + (suspicion / 100) * 1.4;
            playDetectionBeep(pitch, beepVolume);
          }
          this.robotDetectionBeepTimers.set(robot.id, 0);
        } else {
          this.robotDetectionBeepTimers.set(robot.id, beepTimer);
        }
      } else {
        // Reset timer when suspicion is 0 or robot is already chasing
        this.robotDetectionBeepTimers.set(robot.id, 0);
      }

      // ── CEILING CRAWLER special logic ─────────────────────────────
      if (robot.specialAbility === 'ceiling_crawler') {
        // Pick the ceiling closest to the robot's current Y
        const targetCeilingY = FLOOR_CEILING_Y.reduce((best, cy) =>
          Math.abs(cy - robot.position.y) < Math.abs(best - robot.position.y) ? cy : best
        , FLOOR_CEILING_Y[0]);

        // When chasing, aim for the ceiling directly above the player instead
        const chaseCeilingY = robot.getAIState() === 'chase'
          ? FLOOR_CEILING_Y.reduce((best, cy) =>
              Math.abs(cy - this.player.position.y) < Math.abs(best - this.player.position.y) ? cy : best
            , FLOOR_CEILING_Y[0])
          : targetCeilingY;

        // Float toward ceiling (zero gravity, so we control Y velocity directly)
        const yDiff = chaseCeilingY - robot.position.y;
        if (Math.abs(yDiff) > 8) {
          robot.velocity.y = Math.sign(yDiff) * 180;
        } else {
          robot.velocity.y = 0;
          robot.position.y = chaseCeilingY;
        }
      }

      // ── PHANTOM vent traversal logic ──────────────────────────────
      if (robot.specialAbility === 'vent_traversal' && robot.getAIState() === 'chase') {
        for (const vent of this.vents) {
          const entrancePos = (vent as any).getEntrancePosition();
          const exitPos = (vent as any).getExitPosition();

          // Check if robot reached an entrance — teleport to exit (toward player)
          const dxEn = Math.abs(robot.position.x - entrancePos.x);
          const dyEn = Math.abs(robot.position.y - entrancePos.y);
          if (dxEn < 60 && dyEn < 60) {
            // Only use the vent if the exit is closer to the player
            const distExitToPlayer = Math.sqrt(
              Math.pow(exitPos.x - this.player.position.x, 2) +
              Math.pow(exitPos.y - this.player.position.y, 2)
            );
            const distEntranceToPlayer = Math.sqrt(
              Math.pow(entrancePos.x - this.player.position.x, 2) +
              Math.pow(entrancePos.y - this.player.position.y, 2)
            );
            if (distExitToPlayer < distEntranceToPlayer) {
              robot.position.x = exitPos.x;
              robot.position.y = exitPos.y;
              robot.velocity.x = 0;
              robot.velocity.y = 0;
              console.log(`👻 Phantom teleported through vent entrance → exit`);
            }
            break;
          }

          // Check if robot reached an exit — teleport to entrance (toward player)
          const dxEx = Math.abs(robot.position.x - exitPos.x);
          const dyEx = Math.abs(robot.position.y - exitPos.y);
          if (dxEx < 60 && dyEx < 60) {
            const distEntranceToPlayer = Math.sqrt(
              Math.pow(entrancePos.x - this.player.position.x, 2) +
              Math.pow(entrancePos.y - this.player.position.y, 2)
            );
            const distExitToPlayer = Math.sqrt(
              Math.pow(exitPos.x - this.player.position.x, 2) +
              Math.pow(exitPos.y - this.player.position.y, 2)
            );
            if (distEntranceToPlayer < distExitToPlayer) {
              robot.position.x = entrancePos.x;
              robot.position.y = entrancePos.y;
              robot.velocity.x = 0;
              robot.velocity.y = 0;
              console.log(`👻 Phantom teleported through vent exit → entrance`);
            }
            break;
          }
        }
      }

      // ======== COPY FROM HERE ========
      // Skip robot AI entirely while player is in a moving elevator
      if (this.playerInMovingElevator) {
        robot.velocity.x = 0;
        return; // forEach return = skip this robot this frame
      }
      // ======== COPY TO HERE ========

      robot.update(deltaTime);

      // ── Door-opener ability (with teleportation like the player) ──
      if (robot.canOpenDoors && (robot.getAIState() === 'chase' || robot.getAIState() === 'attack')) {
        const DOOR_REACH = 80;
        for (const door of this.doors) {
          if (door.getIsLocked()) continue;
          const ddx = Math.abs(robot.position.x - door.position.x);
          const ddy = Math.abs(robot.position.y - door.position.y);
          if (ddx >= DOOR_REACH || ddy >= 120) continue;

          const doorId  = (door as any).doorId as string;
          const roomX   = (door as any).roomTeleportX as number;
          const roomY   = (door as any).roomTeleportY as number;
          const trackKey = `${robot.id}:${doorId}`;
          const returnPos = this.robotDoorReturnPositions.get(trackKey);

          if (returnPos) {
            // Already inside — teleport back out
            robot.position.x = returnPos.x;
            robot.position.y = returnPos.y;
            robot.velocity.x = 0;
            robot.velocity.y = 0;
            this.robotDoorReturnPositions.delete(trackKey);
            console.log(`🤖 ${robot.id} exited door '${doorId}'`);
          } else if (roomX !== undefined && roomY !== undefined) {
            // Entering — save current position, teleport into room
            if (!door.getIsOpen()) door.open();
            this.robotDoorReturnPositions.set(trackKey, {
              x: robot.position.x,
              y: robot.position.y,
            });
            robot.position.x = roomX;
            robot.position.y = roomY + 30;
            robot.velocity.x = 0;
            robot.velocity.y = 0;
            // Update last known player position so AI keeps chasing inside
            robot.lastKnownPlayerPosition = { ...this.player.position };
            console.log(`🤖 ${robot.id} entered door '${doorId}' → (${roomX}, ${roomY + 30})`);
          }
          break; // only process one door per frame
        }
      }

      // If robot just finished searching and returned to patrol, apply a cooldown
      // so it doesn't immediately spot the player again
      if (robot.returnedFromSearch) {
        robot.returnedFromSearch = false;
        this.robotDetectCooldowns.set(robot.id, 6.0); // 6s grace period after giving up
      }

      // Respawn robots that fall out of bounds
      if (robot.position.y < -500) {
        robot.position.x = 650;
        robot.position.y = 100;
        robot.velocity.x = 0;
        robot.velocity.y = 0;
      }
    });

    // Update doors
    this.doors.forEach(door => {
      door.update(deltaTime);
    });

    // Update vents
    this.vents.forEach(vent => {
      vent.update(deltaTime);
    });

    // Update elevators (key input handled in handleInput where input is fresh)
    this.elevators.forEach(elevator => {
      elevator.update(deltaTime);
    });
    this.updateShaftGates();

    // Sync player position with elevator after it has moved this frame.
    // prevElevY was captured BEFORE physicsEngine.update(), so elevDeltaY = full frame movement.
    if (elevForSync && playerPhysics) {
      const elevDeltaY = elevForSync.position.y - prevElevY;
      if (elevDeltaY < 0) { // elevator moved down this frame
        const dx = Math.abs(this.player.position.x - elevForSync.position.x);
        const dy = Math.abs(this.player.position.y - elevForSync.position.y);
        const insideElev = dx < elevForSync.size.width / 2 + 20 && dy < elevForSync.size.height * 1.5;
        if (insideElev) {
          this.player.position.y += elevDeltaY; // lock player to elevator movement
          this.player.velocity.y = 0;           // kill any residual vertical velocity
          playerPhysics.gravity = 0;            // keep gravity off (collision system may have reset it)
        }
      }
    }

    // Update hiding spots
    this.hidingSpots.forEach(spot => {
      spot.update(deltaTime);
    });

    // Update security cameras (now managed by InteractionManager for interactions)
    this.securityCameras.forEach(camera => {
      camera.update(deltaTime);
    });

    // Update interactive objects
    this.interactiveObjects.forEach(obj => {
      obj.update(deltaTime);
    });

    // Update collectibles and check for collection
    this.collectibles.forEach((collectible, index) => {
      if (!collectible.isItemCollected()) {
        collectible.update(deltaTime);

        // Check if player can collect
        if (collectible.canPickup(this.player)) {
          const wasCollected = collectible.collect(this.player);
          if (wasCollected) {
            const itemType = collectible.getCollectibleType();
            console.log(`✨ Collected ${itemType}!`);
            this.visualEffects.spawnPickupParticles(collectible.position.x, collectible.position.y);

            // Show notification for collectible pickup
            const notificationMessages: Record<string, { message: string; type: 'success' | 'info' }> = {
              lore: { message: 'Document Found!', type: 'info' },
              battery: { message: '+1 Battery Pack', type: 'success' },
              health: { message: '+1 Health Kit', type: 'success' },
              upgrade: { message: 'Upgrade Found!', type: 'success' },
              weapon: { message: 'Weapon Found!', type: 'success' },
              keycard:  { message: '🪪 Keycard Found!',    type: 'success' },
              vhs_tape: { message: '📼 VHS Tape Found!',   type: 'info' },
              pizza:    { message: '🍕 Pizza! +Health',    type: 'success' },
              fuse:     { message: '🔌 Fuse Found!',       type: 'success' },
              badge:    { message: '🏷 Staff Badge Found!', type: 'success' },
              taser: { message: '⚡ Taser Found!', type: 'success' },
              key: { message: '🗝 Key Found!', type: 'success' },
            };
            const notif = notificationMessages[itemType] || { message: `Collected ${itemType}`, type: 'success' };
            store.dispatch(addNotification({
              message: notif.message,
              type: notif.type,
              duration: 3000,
            }));

            // Award XP based on collectible type
            const xpRewards: Record<string, number> = {
              lore: 25,
              battery: 10,
              health: 15,
              upgrade: 50,
              weapon: 75,
              keycard: 60, vhs_tape: 40, pizza: 30, fuse: 50, badge: 55,
              taser: 100,
              key: 50,
            };
            const xpAmount = xpRewards[itemType] || 10;
            upgradeSystem.awardXP(xpAmount, `Collected ${itemType}`);

            // Add items to inventory based on collectible type
            const isLateNight = this.currentNight >= 4;
            const inventoryItemMap: Record<string, { itemId: string; quantity: number }> = {
              battery: { itemId: 'battery_pack',  quantity: 1 },
              health:  { itemId: 'health_kit',    quantity: 1 },
              lore:    { itemId: 'newspaper_1',   quantity: 1 },
              upgrade: { itemId: 'stamina_boost', quantity: 1 },
              weapon:  { itemId: isLateNight ? 'shotgun' : 'pistol', quantity: 1 },
              keycard:  { itemId: 'keycard',     quantity: 1 },
              vhs_tape: { itemId: 'audio_log_1', quantity: 1 },
              pizza:    { itemId: 'health_kit',  quantity: 1 },
              fuse:     { itemId: 'battery_pack',quantity: 1 },
              badge:    { itemId: 'employee_key',quantity: 1 },
              taser:   { itemId: 'pistol',        quantity: 1 }, // taser uses pistol slot
              key:     { itemId: 'employee_key',  quantity: 1 },
            };

            const inventoryItem = inventoryItemMap[itemType];
            if (inventoryItem) {
              inventorySystem.addItem(inventoryItem.itemId, inventoryItem.quantity);
            }

            // Remove mesh (and glow layer) from scene when collected
            const mesh = this.collectibleMeshes[index];
            if (mesh) {
              const glowMesh = (mesh as any).__glowMesh;
              if (glowMesh) { this.scene.remove(glowMesh); glowMesh.geometry.dispose(); (glowMesh.material as THREE.Material).dispose(); }
              this.scene.remove(mesh);
              mesh.geometry.dispose();
              (mesh.material as THREE.Material).dispose();
            }
          }
        }

        // Update collectible mesh position (floating animation) - only if not collected
        if (!collectible.isItemCollected()) {
          const mesh = this.collectibleMeshes[index];
          if (mesh) {
            mesh.position.set(collectible.position.x, collectible.position.y, 1.5);
            const glowMesh = (mesh as any).__glowMesh;
            if (glowMesh) glowMesh.position.set(collectible.position.x, collectible.position.y, 1.4);
          }
        }
      }
    });

    // Update player mesh position and sprite
    this.playerMesh.position.set(
      this.player.position.x,
      this.player.position.y,
      0
    );

    // Swap player texture based on animation state
    const anim = this.player.getCurrentAnimation();
    const isWalking = anim === 'walk' || anim === 'run' || anim === 'crouch_walk';
    const isJumping = anim === 'jump';

    if (isWalking) {
      // Advance walk frame animation
      this.walkFrameTimer += deltaTime;
      if (this.walkFrameTimer >= this.WALK_FRAME_DURATION) {
        this.walkFrameTimer = 0;
        this.walkFrameIndex = (this.walkFrameIndex + 1) % this.playerTextures.walkFrames.length;
      }
      const walkTex = this.playerTextures.walkFrames[this.walkFrameIndex];
      if (walkTex) {
        (this.playerMesh.material as THREE.MeshBasicMaterial).map = walkTex;
        (this.playerMesh.material as THREE.MeshBasicMaterial).needsUpdate = true;
        this.currentPlayerTexture = 'walk';
      }
    } else {
      // Reset walk frame when not walking
      this.walkFrameTimer = 0;
      this.walkFrameIndex = 0;
      const targetTexture = isJumping ? 'jump' : 'idle';
      if (targetTexture !== this.currentPlayerTexture) {
        const tex = targetTexture === 'jump' ? this.playerTextures.jump : this.playerTextures.idle;
        if (tex) {
          (this.playerMesh.material as THREE.MeshBasicMaterial).map = tex;
          (this.playerMesh.material as THREE.MeshBasicMaterial).needsUpdate = true;
          this.currentPlayerTexture = targetTexture;
        }
      }
    }

    // Flip sprite based on facing direction
    const facing = this.player.getFacingDirection();
    this.playerMesh.scale.x = facing === 'left' ? -1 : 1;

    // Update visual effects (particles + shake) BEFORE camera moves
    this.visualEffects.update(deltaTime);
    this.updateSuspicionOverlay();

    // Sync audio listener position with player (world units scaled to audio space)
    // Howler uses a small-unit coordinate system; divide by 100 to keep values sane.
    audioManager.updateListenerPosition(
      this.player.position.x / 100,
      this.player.position.y / 100,
      0
    );

    // Update ambient sound layer based on which floor the player is on
    this.ambientAudio.update(this.player.position.y);

    // CAMERA FOLLOWS PLAYER - smooth tracking
    const targetX = this.player.position.x;
    const targetY = this.player.position.y;

    // Smooth camera interpolation for nice follow effect
    const lerpFactor = 0.1; // Adjust for smoother/snappier follow
    this.camera.position.x += (targetX - this.camera.position.x) * lerpFactor;
    this.camera.position.y += (targetY - this.camera.position.y) * lerpFactor;

    // Apply screen shake offset — must be BEFORE background mesh update so background
    // moves with camera and appears visually static while the game world shakes
    const shake = this.visualEffects.getShakeOffset();
    this.camera.position.x += shake.x;
    this.camera.position.y += shake.y;

    // Move background meshes with camera so they appear STATIC
    // Each mesh's initial position was set relative to camera origin,
    // so we just offset them by the camera's current position
    const camX = this.camera.position.x;
    const camY = this.camera.position.y;
    this.backgroundMeshes.forEach(mesh => {
      // Store original offset on first frame using userData
      if (mesh.userData.bgOffsetX === undefined) {
        mesh.userData.bgOffsetX = mesh.position.x;
        mesh.userData.bgOffsetY = mesh.position.y;
      }
      mesh.position.x = camX + mesh.userData.bgOffsetX;
      mesh.position.y = camY + mesh.userData.bgOffsetY;
    });

    // UPDATE MINIMAP
    this.updateMinimap();

    // UPDATE COLLECTIBLES LIST
    this.updateCollectiblesUI();

    // UPDATE SECURITY CAMERA UI (glitch/motion detection)
    this.updateCameraUI();

    // Update platform mesh positions
    this.platforms.forEach((platform, index) => {
      const mesh = this.platformMeshes[index];
      if (mesh) {
        mesh.position.set(
          platform.position.x,
          platform.position.y,
          0
        );
      }
    });

    // Update robot mesh positions + facing direction
    this.robots.forEach((robot, index) => {
      const mesh = this.robotMeshes[index];
      if (mesh) {
        mesh.position.set(robot.position.x, robot.position.y, 0.5);
        // Flip sprite to match facing direction
        mesh.scale.x = robot.facingDirection === 'left' ? -1 : 1;
      }
    });

    // Sync player state to Redux every frame
    this.syncPlayerStateToRedux();

    // Display stats in console (every 60 frames) - only if physics logging enabled
    if (debugSettings.enablePhysicsLogging && this.gameLoop.getFrameCount() % 60 === 0) {
      const collisionStats = this.collisionSystem.getStats();
      const fps = this.gameLoop.getFPS();

      console.log(
        `FPS: ${fps} | Player: (${Math.round(this.player.position.x)}, ${Math.round(this.player.position.y)}) | ` +
        `Grounded: ${this.physicsEngine.isGrounded(this.player.id)} | ` +
        `Collisions: ${collisionStats.collisionsDetected}`
      );
    }
  }

  /**
   * Sync player entity state to Redux store for UI updates
   */
  private syncPlayerStateToRedux(): void {
    store.dispatch(setHealth(this.player.getHealth()));
    store.dispatch(setStamina(this.player.getStamina()));
    store.dispatch(setFlashlightBattery(this.player.getFlashlightBattery()));
    store.dispatch(setPosition({ x: this.player.position.x, y: this.player.position.y }));
    store.dispatch(setCrouching(this.player.isPlayerCrouching()));
    store.dispatch(setSprinting(this.player.isPlayerSprinting()));
    store.dispatch(setMask(this.player.isMaskOn()));
    store.dispatch(setFlashlight(this.player.isFlashlightOn()));
    store.dispatch(setHiding(this.player.isPlayerHiding()));
  }

  /**
   * Render scene
   */
  public render(): void {
    this.renderer.render(this.scene, this.camera);
  }

  /**
   * Save game progress to database
   */
  private saveGameProgress(checkpointName: string): void {
    try {
      // Calculate play time
      const playTime = Math.floor((Date.now() - this.playStartTime) / 1000); // in seconds

      // Capture current game state
      const saveData = {
        slotNumber: this.currentSaveSlot,
        playerName: 'Player', // Could be customized later
        currentNight: this.currentNight,
        currentLevel: this.id,
        playerHealth: this.player.health,
        playerStamina: this.player.stamina,
        playerMaxHealth: this.player.maxHealth,
        playerMaxStamina: this.player.maxStamina,
        flashlightBattery: this.player.flashlightBattery,
        playerXP: this.player.xp,
        playerLevel: this.player.level,
        playerPosition: JSON.stringify({
          x: this.player.position.x,
          y: this.player.position.y
        }),
        lastCheckpoint: checkpointName,
        playTime: playTime,
        timestamp: Date.now(),
        screenshotPath: null,
      };

      // Save to database
      this.saveManager.createSave(saveData);

      console.log(`💾 Game saved at checkpoint: ${checkpointName}`);
      console.log(`   Play time: ${Math.floor(playTime / 60)}m ${playTime % 60}s`);
      console.log(`   Player: Level ${this.player.level}, XP ${this.player.xp}`);
      console.log(`   Health: ${this.player.health}/${this.player.maxHealth}`);
    } catch (error) {
      console.error('Failed to save game progress:', error);
    }
  }

  /**
   * Respawn the player at the last checkpoint
   */
  private respawnPlayer(): void {
    console.log('🔄 TestScene: Respawning player...');

    // Use checkpoint system to respawn player
    this.checkpointSystem.respawnPlayer(this.player);

    // FORCE update the player mesh position immediately
    this.playerMesh.position.set(
      this.player.position.x,
      this.player.position.y,
      0
    );
    console.log(`   Mesh position updated to: (${this.playerMesh.position.x}, ${this.playerMesh.position.y})`);

    // Restore player health and stamina
    this.player.health = this.player.maxHealth;
    this.player.stamina = this.player.maxStamina;

    // Reset all player physics state
    this.player.velocity.x = 0;
    this.player.velocity.y = 0;
    this.player.grounded = false;

    // Update Redux state
    store.dispatch(setHealth(this.player.health));
    store.dispatch(setStamina(this.player.stamina));
    store.dispatch(setPosition({ x: this.player.position.x, y: this.player.position.y }));

    // Get respawn data for logging
    const respawnData = this.checkpointSystem.getRespawnData();
    console.log(`✅ Player respawned at: ${respawnData.checkpointName}`);
  }

  /**
   * Show a scary cutscene overlay, then trigger game over after a delay.
   */
  private triggerGameOverCutscene(message: string, cause: string = 'Game Over'): void {
    if (this.isGameOverPending) return;
    this.isGameOverPending = true;
    this.ambientAudio.destroy();

    const overlay = document.createElement('div');
    overlay.style.cssText = [
      'position:fixed', 'inset:0', 'background:rgba(0,0,0,0)',
      'display:flex', 'flex-direction:column', 'align-items:center', 'justify-content:center',
      'z-index:99999', 'transition:background 1s ease',
      'font-family:monospace', 'color:#cc0000', 'text-align:center', 'padding:40px',
    ].join(';');
    document.body.appendChild(overlay);

    // Fade in the black background
    requestAnimationFrame(() => { overlay.style.background = 'rgba(0,0,0,0.92)'; });

    // Staggered text reveal
    const lines = [
      { text: message, size: '22px', delay: 800, color: '#cc2200' },
      { text: '— GAME OVER —', size: '42px', delay: 2200, color: '#ff0000' },
      { text: 'The night is over for you.', size: '16px', delay: 3200, color: '#880000' },
    ];

    lines.forEach(({ text, size, delay, color }) => {
      const el = document.createElement('div');
      el.textContent = text;
      el.style.cssText = `font-size:${size};color:${color};opacity:0;transition:opacity 0.8s ease;margin:12px 0;letter-spacing:2px;`;
      overlay.appendChild(el);
      setTimeout(() => { el.style.opacity = '1'; }, delay);
    });

    // After 5 seconds trigger the death callback
    setTimeout(() => {
      overlay.remove();
      if (this.onPlayerDeathCallback) this.onPlayerDeathCallback(cause);
    }, 5000);
  }

  /**
   * Set callback for when player dies
   */
  public setOnPlayerDeath(callback: (cause: string) => void): void {
    this.onPlayerDeathCallback = callback;
  }

  /**
   * Destroy scene
   */
  public destroy(): void {
    console.log('TestScene: Destroying...');
    // Cancel any pending game-over (e.g. stale setTimeout after night completes)
    this.isGameOverPending = true;
    this.onPlayerDeathCallback = null;

    // Remove crosshair and taser mouse listener
    if (this.crosshairEl) { this.crosshairEl.remove(); this.crosshairEl = null; }
    if (this.onMouseDownBound) { window.removeEventListener('mousedown', this.onMouseDownBound); this.onMouseDownBound = null; }
    const crosshairEl = document.getElementById('game-crosshair');
    if (crosshairEl) crosshairEl.remove();

    // Destroy visual effects and ambient audio
    this.visualEffects.destroy();
    this.ambientAudio.destroy();
    const suspOverlay = document.getElementById('suspicion-overlay');
    if (suspOverlay) suspOverlay.remove();
    const dmgFlash = document.getElementById('damage-flash');
    if (dmgFlash) dmgFlash.remove();
    this.suspicionOverlay = null;

    // Close security camera UI if open
    this.closeCameraUI();

    // Remove any active checkpoint notification popup
    if (this.currentNotificationElement && document.body.contains(this.currentNotificationElement)) {
      document.body.removeChild(this.currentNotificationElement);
      this.currentNotificationElement = null;
    }
    // Clear notification queue
    this.notificationQueue = [];
    this.isShowingNotification = false;

    // Clear checkpoint system
    this.checkpointSystem.clear();

    // Close SaveManager (don't close, just leave it open for other scenes)
    // this.saveManager.close();

    // Stop game loop
    this.gameLoop.stop();

    // Remove DOM UI elements
    const minimap = document.getElementById('minimap');
    if (minimap) {
      minimap.remove();
      console.log('Minimap UI removed');
    }

    const collectiblesUI = document.getElementById('collectibles-list');
    if (collectiblesUI) {
      collectiblesUI.remove();
      console.log('Collectibles UI removed');
    }

    const objectivesUI = document.getElementById('objectives-list');
    if (objectivesUI) {
      objectivesUI.remove();
      console.log('Objectives UI removed');
    }

    const interactionPrompt = document.getElementById('interaction-prompt');
    if (interactionPrompt) {
      interactionPrompt.remove();
      console.log('Interaction prompt UI removed');
    }

    // Clear physics and collision systems
    this.physicsEngine.clear();
    this.collisionSystem.clear();

    // Dispose Three.js objects
    this.playerMesh.geometry.dispose();
    (this.playerMesh.material as THREE.Material).dispose();
    // Dispose player textures
    if (this.playerTextures.idle) this.playerTextures.idle.dispose();
    if (this.playerTextures.jump) this.playerTextures.jump.dispose();
    for (const tex of this.playerTextures.walkFrames) tex.dispose();
    this.scene.remove(this.playerMesh);

    // Destroy player
    this.player.destroy();

    this.platformMeshes.forEach((mesh) => {
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
      this.scene.remove(mesh);
    });

    this.robotMeshes.forEach((mesh) => {
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
      this.scene.remove(mesh);
    });

    this.robots.forEach(robot => {
      robot.destroy();
    });

    this.doors.forEach(door => {
      const mesh = door.getMesh();
      if (mesh) {
        this.scene.remove(mesh);
      }
      door.destroy();
    });

    this.vents.forEach(vent => {
      const meshes = vent.getMeshes();
      if (meshes.entrance) this.scene.remove(meshes.entrance);
      if (meshes.exit) this.scene.remove(meshes.exit);
      vent.destroy();
    });

    this.elevators.forEach(elevator => {
      const mesh = elevator.getMesh();
      if (mesh) {
        this.scene.remove(mesh);
      }
      elevator.destroy();
    });

    // Cleanup shaft gates
    this.shaftGates.forEach(gate => {
      if (gate.isRegistered) {
        this.physicsEngine.removeEntity(gate.entity.id);
        this.collisionSystem.removeCollider(gate.entity.id);
      }
      this.scene.remove(gate.mesh);
    });
    this.shaftGates = [];

    // Cleanup call buttons
    this.callButtons.forEach(btn => {
      this.scene.remove(btn.mesh);
    });
    this.callButtons = [];

    // Cleanup hidden containers
    this.hiddenContainers.forEach(c => {
      this.scene.remove(c.mesh);
    });
    this.hiddenContainers = [];

    this.hidingSpots.forEach(spot => {
      spot.destroy();
    });

    this.securityCameras.forEach(camera => {
      camera.destroy();
    });

    this.interactiveObjects.forEach(obj => {
      const mesh = obj.getMesh();
      if (mesh) {
        this.scene.remove(mesh);
      }
      obj.destroy();
    });

    this.renderer.dispose();

    console.log('TestScene: Destroyed');
  }

  /**
   * Pause the game (freeze gameplay and sounds)
   */
  public pause(): void {
    console.log('TestScene: Pausing game...');

    // Pause the game loop (stops updates but keeps rendering)
    this.gameLoop.pause();

    // Remove any active checkpoint notification popup
    if (this.currentNotificationElement && document.body.contains(this.currentNotificationElement)) {
      document.body.removeChild(this.currentNotificationElement);
      this.currentNotificationElement = null;
    }
    // Clear notification queue
    this.notificationQueue = [];
    this.isShowingNotification = false;

    // Hide ALL UI elements created by TestScene
    const minimap = document.getElementById('minimap');
    if (minimap) minimap.style.display = 'none';

    const collectiblesUI = document.getElementById('collectibles-list');
    if (collectiblesUI) collectiblesUI.style.display = 'none';

    const objectivesUI = document.getElementById('objectives-list');
    if (objectivesUI) objectivesUI.style.display = 'none';

    const interactionPrompt = document.getElementById('interaction-prompt');
    if (interactionPrompt) interactionPrompt.style.display = 'none';

    const cluePopup = document.getElementById('clue-popup');
    if (cluePopup) cluePopup.style.display = 'none';

    // Pause all Howler sounds EXCEPT music
    this.pausedSounds = []; // Clear the list
    if (typeof window !== 'undefined' && (window as any).Howler) {
      const Howler = (window as any).Howler;
      // Get all playing sounds
      const sounds = Howler._howls || [];
      sounds.forEach((sound: any) => {
        // Only pause if it's not a music track
        // Music tracks typically have _sprite or loop: true
        // We'll pause all sounds that aren't looping (music is looping)
        if (sound && sound.playing && sound.playing() && !sound._loop) {
          // Get current playback position before pausing
          const currentSeek = typeof sound.seek === 'function' ? sound.seek() : 0;
          sound.pause();
          this.pausedSounds.push({ sound, seek: currentSeek }); // Store sound and position
        }
      });
    }

    console.log(`TestScene: Game paused - UI hidden, ${this.pausedSounds.length} sounds paused, game loop frozen`);
  }

  /**
   * Resume the game (unfreeze gameplay and sounds)
   */
  public resume(): void {
    console.log('TestScene: Resuming game...');

    // Resume the game loop
    this.gameLoop.resume();

    // Show ALL UI elements again
    const minimap = document.getElementById('minimap');
    if (minimap) minimap.style.display = 'block';

    const collectiblesUI = document.getElementById('collectibles-list');
    if (collectiblesUI) collectiblesUI.style.display = 'block';

    const objectivesUI = document.getElementById('objectives-list');
    if (objectivesUI) objectivesUI.style.display = 'block';

    const interactionPrompt = document.getElementById('interaction-prompt');
    if (interactionPrompt) interactionPrompt.style.display = 'block';

    const cluePopup = document.getElementById('clue-popup');
    if (cluePopup) cluePopup.style.display = 'block';

    // Resume all paused Howler sounds from their stored positions
    this.pausedSounds.forEach(({ sound, seek }) => {
      if (sound && typeof sound.play === 'function') {
        const soundId = sound.play(); // Start playing the sound
        if (typeof sound.seek === 'function' && seek > 0) {
          sound.seek(seek, soundId); // Resume from the stored position
        }
      }
    });

    console.log(`TestScene: Game resumed - UI shown, ${this.pausedSounds.length} sounds resumed from saved positions, game loop running`);
    this.pausedSounds = []; // Clear the list
  }

  /**
   * Queue a checkpoint notification - shows one at a time
   */
  private queueNotification(checkpointName: string): void {
    // Add to queue
    this.notificationQueue.push({ checkpointName });
    console.log(`📋 Notification queued: ${checkpointName} (Queue size: ${this.notificationQueue.length})`);

    // If not currently showing a notification, show the next one
    if (!this.isShowingNotification) {
      this.showNextNotification();
    }
  }

  /**
   * Show the next notification from the queue
   */
  private showNextNotification(): void {
    // Check if there are notifications in the queue
    if (this.notificationQueue.length === 0) {
      this.isShowingNotification = false;
      return;
    }

    // Get the next notification
    const notification = this.notificationQueue.shift();
    if (!notification) {
      this.isShowingNotification = false;
      return;
    }

    this.isShowingNotification = true;

    // Create visual popup notification
    const popup = document.createElement('div');
    popup.style.position = 'fixed';
    popup.style.top = '30%'; // Lower position to avoid blending with clock
    popup.style.left = '50%';
    popup.style.transform = 'translate(-50%, -50%)';
    popup.style.background = 'rgba(0, 255, 0, 0.9)';
    popup.style.color = '#000';
    popup.style.padding = '20px 40px';
    popup.style.borderRadius = '10px';
    popup.style.fontSize = '24px';
    popup.style.fontFamily = 'monospace';
    popup.style.fontWeight = 'bold';
    popup.style.zIndex = '10000';
    popup.style.boxShadow = '0 0 30px rgba(0, 255, 0, 0.8)';
    popup.innerHTML = `
      ✅ CHECKPOINT: ${notification.checkpointName}<br>
      <span style="font-size: 16px; color: #333;">💾 Progress Saved</span>
    `;
    document.body.appendChild(popup);
    this.currentNotificationElement = popup;

    console.log(`📢 Showing notification: ${notification.checkpointName}`);

    // Remove after 3 seconds and show next notification
    setTimeout(() => {
      if (document.body.contains(popup)) {
        document.body.removeChild(popup);
      }
      this.currentNotificationElement = null;

      // Show next notification in queue (if any)
      this.showNextNotification();
    }, 3000);
  }
}
